// ============ Domain DTOs ============
// Types métier utilisés par le frontend et les interfaces.
// Indépendants de Supabase — les adapters mappent vers ces types.

// Motif de blocage d'un retrait de BL — décidé par @erp/domain/sales. `import
// type` : effacé à la compilation, donc aucune dépendance de bundle ajoutée
// aux apps qui embarquent cette lib.
import type { DeliveryRemovalBlocker, PackagingUnit } from '@erp/domain/sales';

export type { PackagingUnit } from '@erp/domain/sales';

// ---- Tiers ----

export type ThirdPartyType = 'company' | 'individual';

export interface Client {
  id: string;
  code: string;
  name: string;
  type: ThirdPartyType;
  taxId: string | null;
  address: string | null;
  city: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  carRegistrationNumber: string | null;
  defaultDiscountPct: number;
  paymentTermsDays: number;
  notes: string | null;
  isDefault: boolean;
  proClientId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClientInput {
  /** Si omis, le repository auto-génère le code depuis les settings. */
  code?: string;
  name: string;
  type: ThirdPartyType;
  taxId?: string;
  address?: string;
  city?: string;
  phone?: string;
  fax?: string;
  email?: string;
  carRegistrationNumber?: string;
  defaultDiscountPct?: number;
  paymentTermsDays?: number;
  notes?: string;
  isDefault?: boolean;
  proClientId?: string | null;
}

export type UpdateClientInput = Partial<CreateClientInput>;

export interface ClientFilters {
  search?: string;
  type?: ThirdPartyType;
  city?: string;
}

export interface Supplier {
  id: string;
  code: string;
  name: string;
  type: ThirdPartyType;
  taxId: string | null;
  address: string | null;
  city: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplierInput {
  /** Si omis, le repository auto-génère le code depuis les settings. */
  code?: string;
  name: string;
  type: ThirdPartyType;
  taxId?: string;
  address?: string;
  city?: string;
  phone?: string;
  fax?: string;
  email?: string;
  notes?: string;
}

export type UpdateSupplierInput = Partial<CreateSupplierInput>;

export interface SupplierFilters {
  search?: string;
  type?: ThirdPartyType;
}

// ---- Représentants (sales reps) ----
// Pas d'authentification. Fiche interne liée M:N aux clients et
// optionnellement rattachée à un document de vente. Jamais affichée dans les PDF.

export interface Representative {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
  /** Pourcentage [0..100] appliqué au bénéfice des documents pour le solde de commission. */
  commissionRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRepresentativeInput {
  name: string;
  phone?: string;
  email?: string;
  notes?: string;
  commissionRate?: number;
}

export type UpdateRepresentativeInput = Partial<CreateRepresentativeInput>;

export interface RepresentativeFilters {
  search?: string;
}

export interface ClientRepresentativeLink {
  clientId: string;
  representativeId: string;
  createdAt: string;
}

// ---- Articles ----

export interface ArticleCategory {
  id: string;
  name: string;
  parentId: string | null;
  imageUrl: string | null;
}

export interface CreateArticleCategoryInput {
  name: string;
  parentId?: string;
  imageUrl?: string;
}

export interface ArticleFamily {
  id: string;
  name: string;
}

export interface ArticleBrand {
  id: string;
  name: string;
  imageUrl: string | null;
}

export interface CreateArticleBrandInput {
  name: string;
  imageUrl?: string;
}

export interface Article {
  id: string;
  code: string;
  barcode: string | null;
  name: string;
  description: string | null;
  categoryId: string | null;
  family: string | null;
  subFamily: string | null;
  brand: string | null;
  /** Unité de base de l'article (`pcs`, `kg`, `m`…). Le prix de vente est le
   *  prix de CETTE unité. Voir aussi `packagingUnits`. */
  unit: string;
  /**
   * Unités de conditionnement en plus de l'unité de base : « Carton = 12
   * pièces », « Lot = 6 pièces ». Chaque entrée porte un `factor` (nombre de
   * pièces de base). Absent/vide = article mono-unité (comportement historique,
   * lignes legacy comprises). Le prix d'un conditionnement est TOUJOURS dérivé
   * (prix pièce × facteur), jamais stocké. Voir @erp/domain/sales
   * (packaging-units). L'adapter renvoie toujours un tableau (jamais undefined). */
  packagingUnits?: PackagingUnit[];
  purchasePriceHt: number;
  supplierDiscount: number;
  salePriceHt: number;
  salePriceTtc: number;
  vatRate: number;
  stockMin: number;
  /** false = stock non suivi (disponibilité NULL), true = suivi, zéro compris. */
  stockTrackingEnabled: boolean;
  initialStock: number;
  images: string[];
  imageUrl: string | null;
  isActive: boolean;
  isInvoiceable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateArticleInput {
  /** Clé durable optionnelle pour rejouer exactement la création atomique. */
  clientRequestId?: string;
  /** Si omis ou vide, le repository auto-génère le code depuis les settings. */
  code?: string;
  name: string;
  /** Si omis/vide, le barcode reste null. Si fourni, utilisé tel quel. */
  barcode?: string;
  /** Si true, ignore `barcode` et consomme le compteur auto-increment. */
  autoGenerateBarcode?: boolean;
  description?: string;
  categoryId?: string;
  family?: string;
  subFamily?: string;
  brand?: string;
  unit?: string;
  /** Unités de conditionnement (Carton, Lot…). Omis/vide = mono-unité. */
  packagingUnits?: PackagingUnit[];
  purchasePriceHt?: number;
  supplierDiscount?: number;
  salePriceHt?: number;
  salePriceTtc?: number;
  vatRate?: number;
  stockMin?: number;
  stockTrackingEnabled?: boolean;
  /**
   * Stocks d'ouverture atomiques. Chaque quantité crée un mouvement `in` dans
   * la même transaction que l'article; ne pas enchaîner `createMovement` côté UI.
   */
  initialStocks?: { warehouseId: string; quantity: number }[];
  /** @deprecated Valeur historique sans dépôt; ne représente pas le registre. */
  initialStock?: number;
  images?: string[];
  imageUrl?: string;
  isActive?: boolean;
  isInvoiceable?: boolean;
}

export type UpdateArticleInput = Partial<
  Omit<CreateArticleInput, 'clientRequestId' | 'initialStocks'>
>;

// ---- Product variants (structured option types + values) ----

export interface ArticleOptionValue {
  id: string;
  optionTypeId: string;
  value: string;
  /** Hex colour (#RRGGBB) or image URL for a colour swatch; null = plain pill. */
  swatch: string | null;
  displayOrder: number;
}

export interface ArticleOptionType {
  id: string;
  articleId: string;
  name: string;
  displayOrder: number;
  values: ArticleOptionValue[];
}

export interface ArticleVariant {
  id: string;
  articleId: string;
  sku: string | null;
  barcode: string | null;
  /** Per-variant price overrides; null = fall back to the article / storefront price. */
  salePriceHt: number | null;
  salePriceTtc: number | null;
  /** Own purchase price (HT); null = the article's applies. Also null for a
   *  role without `costs.view`: the authorized view masks it. */
  purchasePriceHt: number | null;
  initialStock: number;
  /** Signed ledger balance of this variant (opening declaration plus later
   *  movements). Display this; `initialStock` is only the declaration. */
  stockQuantity: number;
  imageUrl: string | null;
  isActive: boolean;
  displayOrder: number;
  /** option_value ids that define this combination. */
  optionValueIds: string[];
}

/** The full option-type + variant graph for one article. */
export interface ArticleVariantMatrix {
  optionTypes: ArticleOptionType[];
  variants: ArticleVariant[];
}

export interface SaveArticleOptionValueInput {
  id?: string;
  value: string;
  swatch?: string | null;
  displayOrder?: number;
}

export interface SaveArticleOptionTypeInput {
  id?: string;
  name: string;
  displayOrder?: number;
  values: SaveArticleOptionValueInput[];
}

export interface SaveArticleVariantInput {
  id?: string;
  sku?: string | null;
  barcode?: string | null;
  /** Take the next tenant barcode at save when `barcode` is blank — the
   *  adapter consumes the same counter as for a new article. */
  autoGenerateBarcode?: boolean;
  salePriceHt?: number | null;
  salePriceTtc?: number | null;
  /** Own purchase price (HT). `undefined` leaves the stored value untouched —
   *  a caller that cannot see costs must never send this key; `null` clears
   *  the override so the article's price applies again. */
  purchasePriceHt?: number | null;
  /** Opening declaration of the variant. Sent unchanged for a variant that
   *  already exists — `stockQuantity` is what moves its stock. */
  initialStock?: number;
  /** The AVAILABLE stock this variant must end up with. The server reconciles
   *  the ledger with it: the opening line for a variant that has never moved,
   *  an adjustment for one that has. Omit to leave the stock alone. */
  stockQuantity?: number;
  imageUrl?: string | null;
  isActive?: boolean;
  displayOrder?: number;
  /** Ids of already-persisted option values that define this variant. */
  optionValueIds?: string[];
  /** For a not-yet-persisted value: index into optionTypes[typeIndex].values[valueIndex].
   *  The adapter resolves these to ids after inserting the values. */
  optionValueRefs?: { typeIndex: number; valueIndex: number }[];
}

// ---- Attribute library (reusable option vocabularies) ----

export interface AttributePresetValue {
  id: string;
  presetId: string;
  value: string;
  /** Hex colour (#RRGGBB) or image URL; null = plain pill. */
  swatch: string | null;
  displayOrder: number;
}

/**
 * A vocabulary prepared in advance ("Pointure — 36…46").
 *
 * Applying one to an article COPIES its values; there is no foreign key back,
 * so renaming a value here can never rewrite the label frozen on a past
 * document.
 */
export interface AttributePreset {
  id: string;
  name: string;
  displayOrder: number;
  values: AttributePresetValue[];
}

export interface SaveAttributePresetValueInput {
  id?: string;
  value: string;
  swatch?: string | null;
  displayOrder?: number;
}

export interface SaveAttributePresetInput {
  name: string;
  displayOrder?: number;
  values: SaveAttributePresetValueInput[];
}

/**
 * One stored option value, flattened with the article that declares it.
 *
 * The reusable "attribute library" is a PROJECTION over these rows rather than
 * a table of its own: an attribute exists because some article uses it, so the
 * usage counts are true by construction and nothing can drift. The folding
 * itself lives in `@erp/domain/storefront` — this is only the raw shape.
 */
export interface ArticleAttributeRow {
  articleId: string;
  name: string;
  value: string;
  swatch: string | null;
}

export interface SaveArticleVariantsInput {
  optionTypes: SaveArticleOptionTypeInput[];
  variants: SaveArticleVariantInput[];
}

/** Champs texte d'un article sur lesquels une recherche peut porter. */
export type ArticleSearchField = 'code' | 'name' | 'barcode' | 'description';

/** Ordre canonique des champs balayés quand l'appelant n'en restreint aucun. */
export const ARTICLE_SEARCH_FIELDS: readonly ArticleSearchField[] = [
  'name',
  'code',
  'barcode',
  'description',
] as const;

export interface ArticleFilters {
  /** Match concrete variant attributes/SKU/barcode as well as parent text. */
  searchVariants?: boolean;
  /** AND across configured attributes, on the same concrete variant. */
  variantAttributes?: Record<string, string>;
  activeVariantsOnly?: boolean;
  /**
   * Document pickers only need sale/catalog data. Setting this to false keeps
   * purchase_price_ht and supplier_discount out of the network payload and
   * query cache; callers must still enforce their own RBAC before querying.
   * Omitted preserves the historical full-row projection.
   */
  includeCosts?: boolean;
  /**
   * Rafraîchissement DELTA du miroir hors-ligne : ne rend que les lignes dont
   * `updated_at` est STRICTEMENT postérieur à cet instant ISO.
   *
   * Le miroir desktop se re-paginait entièrement toutes les 30 minutes (~5 Mo
   * par cycle, 24 h/24) alors que quelques lignes seulement avaient bougé.
   * Un trigger `set_updated_at` maintient la colonne sur chaque UPDATE, donc
   * le delta est fiable.
   *
   * ⚠ Ne dit RIEN des lignes SUPPRIMÉES : sans tombstone, une suppression faite
   * depuis un autre poste reste invisible du delta. C'est pourquoi il ne
   * remplace pas la re-semence complète, il l'espace.
   */
  updatedSince?: string;
  search?: string;
  /**
   * Restreint `search` à ces champs. Absent ou vide = tous les champs
   * (`ARTICLE_SEARCH_FIELDS`), c'est-à-dire le comportement historique.
   */
  searchFields?: ArticleSearchField[];
  /**
   * Ces articles précisément, par id. Sert aux écrans qui connaissent déjà
   * les articles dont ils ont besoin (les lignes d'un document) et ne peuvent
   * pas compter sur le catalogue chargé, plafonné à 1000 lignes. Liste vide =
   * aucun article (jamais « tous »).
   *
   * ⚠ Une longue liste est découpée en plusieurs requêtes (plafond d'URL) :
   * ne pas la combiner avec `limit`/`offset`, la pagination s'appliquerait à
   * chaque lot.
   */
  ids?: string[];
  categoryId?: string;
  // These loose VARCHAR columns can hold either a UUID (new rows) or a raw
  // name string (legacy/imported rows). Callers pass `[id, name]` so both
  // representations match.
  family?: string | string[];
  subFamily?: string | string[];
  brand?: string | string[];
  unit?: string | string[];
  isActive?: boolean;
  isInvoiceable?: boolean;
  /** Restricts catalogue reads to stock-tracked or explicitly untracked rows. */
  stockTrackingEnabled?: boolean;
  offset?: number;
  limit?: number;
  sort?: DocumentSort;
}

/**
 * Projection minimale du catalogue pour valoriser le stock.
 *
 * Même intention que `DocumentBalanceRow` plus bas : la bande de stats de la
 * page Articles a besoin du prix d'achat et du seuil de CHAQUE article, pas de
 * leurs descriptions ni de leurs images. Sur un gros catalogue, le miroir
 * complet `['articles', undefined]` pèse ~3,5 Mo — ces cinq colonnes en font un
 * dixième, et le calcul de valorisation reste dans `libs/domain/inventory`.
 */
export interface ArticleValuationRow {
  id: string;
  stockTrackingEnabled: boolean;
  /** Prix d'achat brut HT (0 = coût inconnu, l'article n'est pas valorisé). */
  purchasePriceHt: number;
  /** Remise fournisseur en % — le coût NET s'en déduit via `netUnitCostHt`. */
  supplierDiscount: number;
  /** Seuil d'alerte. 0 = pas de seuil configuré. */
  stockMin: number;
  isActive: boolean;
}

/**
 * Agrégats du catalogue — ce que le tableau de bord et les pages de
 * référentiels lisaient jusqu'ici en rapatriant le catalogue ENTIER (4,2 Mo).
 *
 * ⚠ Aucune règle de gestion n'est décidée côté serveur : `stockMinArticles`
 * liste les articles qui PORTENT un seuil (c'est l'appelant qui compare), et
 * `stockCostBuckets` donne des quantités par couple (prix, remise) — c'est
 * `netUnitCostHt` (@erp/domain/sales) qui en tire un coût. Le regroupement est
 * exact : le coût unitaire ne dépend que de ces deux colonnes.
 */
export interface CatalogStats {
  /** Nombre total d'articles, actifs et inactifs confondus. */
  total: number;
  active: number;
  byCategory: { categoryId: string; count: number }[];
  byFamily: { family: string; count: number }[];
  /** Articles dont `stockMin > 0`, avec leur quantité toutes zones confondues. */
  stockMinArticles: {
    id: string;
    code: string;
    name: string;
    unit: string;
    stockMin: number;
    stockTrackingEnabled: boolean;
    isActive: boolean;
    quantity: number;
  }[];
  /** Quantités en stock regroupées par couple (prix d'achat, remise). */
  stockCostBuckets: {
    purchasePriceHt: number;
    supplierDiscount: number;
    quantity: number;
  }[];
}

// ---- Ventes ----

export type SalesDocType =
  | 'quote'
  | 'order'
  | 'delivery'
  | 'invoice'
  | 'credit_note';
export type SalesCustomerSource = 'local' | 'online' | 'pro';

// ---- Shared document custom properties ----

export type DocumentPropertyDocumentType =
  | `sales.${SalesDocType}`
  | `purchase.${PurchaseDocType}`
  | 'stock.transfer'
  | 'stock.exit'
  | 'stock.entry';

export type DocumentPropertyInputType =
  | 'text'
  | 'multiline'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select';

export type DocumentPropertyValue = string | number | boolean | null;
export type DocumentPropertyValues = Record<string, DocumentPropertyValue>;

export interface DocumentPropertyDefinition {
  id: string;
  documentType: DocumentPropertyDocumentType;
  propertyKey: string;
  label: string;
  inputType: DocumentPropertyInputType;
  selectOptions: string[];
  isRequired: boolean;
  showOnScreen: boolean;
  showOnPrint: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocumentPropertyDefinitionInput {
  documentType: DocumentPropertyDocumentType;
  propertyKey: string;
  label: string;
  inputType: DocumentPropertyInputType;
  selectOptions?: string[];
  isRequired?: boolean;
  showOnScreen?: boolean;
  showOnPrint?: boolean;
  sortOrder?: number;
}

export type UpdateDocumentPropertyDefinitionInput = Partial<
  Omit<CreateDocumentPropertyDefinitionInput, 'documentType'>
> & { isActive?: boolean };

/** Une remise s'exprime en pourcentage ('pct') ou en montant fixe DT ('amount'). */
export type DiscountMode = 'pct' | 'amount';

/**
 * Projection ÉTROITE d'un document de vente, pour les écrans qui n'en font que
 * des sommes et des séries — le tableau de bord.
 *
 * Celui-ci téléchargeait les documents ENTIERS de cinq types sur 24 mois
 * (`select=*`, ~10 Mo chez le plus gros tenant) pour n'en additionner que des
 * `totalTtc` par date. Pire : `listDocuments` enrichit chaque page en relisant
 * les tiers (`clients?select=*&id=in.(…)`), soit ~500 ko de plus dont aucun
 * widget ne lit quoi que ce soit.
 *
 * ⚠ Ce type est le CONTRAT : ajouter un champ à un widget du tableau de bord
 * ne compile que si le champ est ici ET dans la projection SQL. C'est ce qui
 * empêche une colonne oubliée de devenir un `undefined` silencieux dans une
 * somme.
 *
 * Il est volontairement assignable à `RevenueDocument` (@erp/domain/dashboard) :
 * la règle « le BL fait le chiffre, la facture qui en découle ne le refait pas »
 * reste dans le domaine.
 */
export interface SalesDocumentSummary {
  id: string;
  number: string;
  docType: SalesDocType;
  docDate: string;
  clientId: string;
  /** Physical store where the sale originated; null for company/online orders. */
  originStoreId: string | null;
  /** 'local' | 'storefront' | 'pos' | 'social' | 'phone' | 'manual' */
  channel?: string | null;
  isInvoiced: boolean;
  parentDocId: string | null;
  parentDocIds: string[];
  totalTtc: number;
  /** Départage les ex æquo de `docDate` : sans lui, « les plus récents »
   *  se réordonnent à chaque rafraîchissement entre documents du même jour. */
  createdAt: string;
}

/**
 * Empreinte d'un ENSEMBLE de documents : combien, et le plus récemment touché.
 *
 * Sert à savoir s'il faut re-télécharger, sans re-télécharger. Le tableau de
 * bord se rafraîchit toutes les 60 s en invalidant ses requêtes, ce qui
 * court-circuite le `staleTime` : tout le jeu de données repartait sur le fil
 * chaque minute, soit ~1,6 Go par jour et par onglet laissé ouvert.
 *
 * ⚠ Le couple (nombre, dernière modification) détecte les TROIS cas, et c'est
 * ce qui rend l'optimisation EXACTE plutôt qu'un pari sur la fraîcheur :
 *   - suppression        → `count` baisse ;
 *   - création / édition → `maxUpdatedAt` avance (trigger `set_updated_at`) ;
 *   - suppression ET création dans la même minute → `count` est stable mais
 *     `maxUpdatedAt` avance, puisque la ligne créée porte l'instant courant.
 */
export interface DocumentWindowSignature {
  count: number;
  /** `null` quand l'ensemble est vide. */
  maxUpdatedAt: string | null;
}

/** Pendant achats de `SalesDocumentSummary` — mêmes raisons, mêmes garanties. */
export interface PurchaseDocumentSummary {
  id: string;
  number: string;
  docType: PurchaseDocType;
  docDate: string;
  supplierId: string;
  isInvoiced: boolean;
  totalTtc: number;
  /** Voir `SalesDocumentSummary.createdAt` : c'est la clé de tri « les plus
   *  récents », `docDate` n'étant que la retombée. ⚠ Optionnel parce que les
   *  caches persistés d'AVANT son ajout portent des lignes sans elle. */
  createdAt?: string;
}

export interface SalesDocument {
  id: string;
  docType: SalesDocType;
  /** Empty string when the document came from the storefront — use onlineCustomerId to identify the buyer. */
  number: string;
  /**
   * True only on the OPTIMISTIC row of an offline create whose number is a
   * local proposal not yet claimed by the server. Server rows never set it.
   * The lists render a small "provisoire" hint off this flag.
   */
  numberProvisional?: boolean;
  clientId: string;
  /** Physical store where the sale originated; null for company/online orders. */
  originStoreId: string | null;
  /**
   * Entrepôt réellement débité par ce BL — posé par le RPC atomique depuis le
   * MÊME `p_warehouse_id` que ses mouvements de stock, jamais par l'UI. `null` :
   * document sans ligne suivie, ou créé avant l'ajout de la colonne.
   */
  stockWarehouseId?: string | null;
  /** Caisse (tiroir) depuis laquelle le ticket POS a été créé ; null hors POS. */
  cashRegisterId?: string | null;
  customerSource: SalesCustomerSource;
  /** Populated when the order came from the online storefront */
  onlineCustomerId?: string | null;
  proCustomerId?: string | null;
  proClientId?: string | null;
  /** 'local' | 'storefront' | 'pos' | 'social' | 'phone' | 'manual' */
  channel?: string | null;
  representativeId: string | null;
  carRegistrationNumber: string | null;
  docDate: string;
  validityDate: string | null;
  parentDocId: string | null;
  parentDocIds: string[];
  isInvoiced: boolean;
  /** Lifecycle for storefront orders (null for local/POS/manual). */
  storefrontOrderStatus?:
    | import('./storefront-types').StorefrontOrderStatus
    | null;
  /** How the storefront shopper chose to pay (null for local/POS/manual). */
  paymentMethod?: import('./storefront-types').StorePaymentMethod | null;
  /** Online payment state — 'paid' is only ever set server-side after gateway verification. */
  paymentStatus?: import('./storefront-types').StorefrontPaymentStatus | null;
  /** Linked ProSpace commande when this sales document was generated from a Pro order. */
  proCommandeCode?: string | null;
  /** Document exonéré de TVA (client sous attestation d'exonération / régime
   *  suspensif). Les lignes sont ENREGISTRÉES à 0 % (données cohérentes au
   *  repos : totalVat = 0, TTC = HT) — ce flag est le marqueur d'affichage
   *  (badge, mention PDF) et permet de restaurer les taux catalogue quand on
   *  dés-exonère à l'édition. Voir exemptLinesFromVat (@erp/domain/sales). */
  vatExempt?: boolean;
  totalHt: number;
  /** Somme des remises lignes. */
  totalDiscount: number;
  /** Remise globale document : mode + valeur saisie, et montant DT résolu. */
  documentDiscountMode: DiscountMode;
  documentDiscountValue: number;
  documentDiscount: number;
  totalVat: number;
  totalTtc: number;
  withholdingAmount: number;
  stampAmount: number;
  /** FODEC figé sur le document : taux (% du HT net, 0 = sans FODEC) et
   *  montant en DT, inclus dans totalVat/totalTtc. ⚠ Optionnels parce que
   *  les caches persistés d'AVANT leur ajout portent des lignes sans eux ;
   *  l'adapter les renvoie toujours. */
  fodecRate?: number;
  fodecAmount?: number;
  /** Delivery fee charged to the customer at checkout (storefront orders).
   *  Frozen at order time — captured from store_settings.delivery_fee. */
  deliveryFee: number;
  netToPay: number;
  notes: string | null;
  customProperties?: DocumentPropertyValues;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: SalesDocumentLine[];
  /**
   * Applications d'avoirs chargées pour ce document. Sur une facture, ce sont
   * les corrections qui composent son net ; sur un avoir, ce sont ses usages.
   * Les montants stockés sont positifs et l'UI/PDF les projettent en négatif.
   */
  creditApplications?: SalesCreditApplication[];
  /** Somme active déjà absorbée dans le total net d'une ou plusieurs factures. */
  invoiceAppliedTtc?: number;
  /** Part déjà consommée par des allocations de paiement négatives historiques. */
  paymentAppliedTtc?: number;
  /**
   * Statut agrégé par une projection dédiée qui ne révèle aucune facture
   * consommatrice. Il reste donc fiable pour un rôle autorisé à consulter
   * l'avoir mais pas les factures qui l'ont absorbé.
   */
  creditApplicationStatus?: SalesCreditApplicationStatus;
  /** Fait d'annulation immuable d'un avoir, projeté sans ajouter de statut au document. */
  creditCancelledAt?: string | null;
  creditCancelledBy?: string | null;
  creditCancellationReason?: string | null;
  client?: Client;
  onlineCustomer?: import('./storefront-types').OnlineCustomer | null;
  proCustomer?: import('./pro-types').ProCustomer | null;
  proClient?: import('./pro-types').ProClient | null;
}

export interface SalesDocumentLine {
  id: string;
  documentId: string;
  lineNumber: number;
  articleId: string | null;
  /** Chosen product variant, when the line is for a variant SKU. */
  variantId: string | null;
  /** Frozen variant label snapshot (e.g. "Rouge / M") for receipts and history. */
  variantLabel: string | null;
  /**
   * Libellé de l'unité de vente choisie (« Carton »…), figé sur la ligne comme
   * `variantLabel`. `null`/absent = unité de base de l'article (pièce). Sert à
   * l'écran et au PDF ; le prix effectif est déjà dans `unitPriceHt`. L'adapter
   * le renvoie toujours (null si base) ; optionnel pour les lignes legacy et
   * les littéraux de test. */
  unitLabel?: string | null;
  /**
   * Facteur de l'unité choisie = nombre de pièces de base par unité vendue.
   * `1`/absent = unité de base (défaut, articles mono-unité et lignes legacy).
   * Figé pour que le stock décompte le bon nombre de pièces même après
   * transformation devis→BL. L'adapter le renvoie toujours (1 si base). */
  unitFactor?: number;
  description: string;
  quantity: number;
  unitPriceHt: number;
  /** Ancre TTC de la ligne (prix unitaire TTC catalogue, millimes). `null`
   *  pour une ligne HT classique — voir DocumentLineInput.unitPriceTtc dans
   *  @erp/domain/sales. */
  unitPriceTtc: number | null;
  purchasePrice: number;
  discountPct: number;
  /** 'pct' (défaut) ou 'amount' — quelle saisie pilote la remise ligne. */
  discountMode: DiscountMode;
  /** Montant de la remise en DT quand discountMode === 'amount'. */
  discountAmount: number;
  vatRate: number;
  totalHt: number;
  totalTtc: number;
  /**
   * Document d'origine de la ligne — le bon de livraison dont une facture
   * groupée l'a reprise. C'est ce qui permet d'afficher et d'imprimer la
   * facture BL par BL sans deviner la frontière entre deux BL.
   *
   * `null` sur une ligne saisie directement, et sur toute facture enregistrée
   * AVANT la colonne (migration 20260803100000) : le regroupement retombe
   * alors sur le réalignement de @erp/domain/sales.
   */
  sourceDocId?: string | null;
  /** Ligne exacte du BL/facture retournée par une ligne d'avoir. */
  sourceLineId?: string | null;
  /** Read-only enrichment: the linked article's current code/name, joined by
   *  the repository on load. Lets printing show the article reference without
   *  depending on a (capped) client-side article catalog. Undefined on
   *  locally-built documents that never round-tripped through the data layer. */
  articleCode?: string | null;
  articleName?: string | null;
  /** Read-only enrichment: the linked article's `is_invoiceable` flag, joined
   *  on load (same reason as articleCode/articleName). Lets the BL→facture
   *  transform decide invoiceability without the capped client catalog, which
   *  silently drops articles past 1000 rows. `null` when the article was
   *  deleted or the line never round-tripped through the data layer. */
  articleIsInvoiceable?: boolean | null;
  /** Read-only enrichment: the linked article's unit of measure (`pcs`, `kg`,
   *  `m`…), joined on load. Drives the whole-unit quantity rule (`box` only;
   *  `pcs` and measurable/custom units accept decimals) without the capped
   *  client catalog — see isWholeUnitQuantity in @erp/domain/sales. `null`
   *  when the article was deleted or the line never round-tripped through the
   *  data layer. */
  articleUnit?: string | null;
  /** Read-only enrichment: the linked article's image (`image_url`, else
   *  `images[0]`), already resolved to an absolute public URL by the data
   *  layer. Lets a line render a thumbnail without mounting the client
   *  catalog, which is capped at 1000 articles and silently blanks every line
   *  past it. Only populated by the single-document read path
   *  (`getDocumentById`); list and child-document reads leave it undefined.
   *  `null` when the article has no picture, was deleted, or the line is free
   *  text with no article at all. */
  articleImageUrl?: string | null;
}

export interface SalesCreditApplicationLine {
  id: string;
  applicationId: string;
  creditNoteLineId: string;
  sourceLineId: string | null;
  lineNumber: number;
  articleId: string | null;
  description: string;
  appliedQuantity: number;
  unitPriceHt: number;
  unitPriceTtc: number | null;
  vatRate: number;
  appliedHtBeforeDiscount: number;
  appliedDiscount: number;
  appliedHt: number;
  appliedDocumentDiscount: number;
  appliedNetHt: number;
  appliedVat: number;
  appliedTtc: number;
  appliedBenefit: number | null;
}

/** Fait persistant d'utilisation d'un avoir de BL sur une facture. */
export interface SalesCreditApplication {
  id: string;
  creditNoteId: string;
  creditNoteNumber: string | null;
  invoiceId: string;
  sourceDeliveryId: string;
  clientRequestId: string;
  consumedFromTtc: number;
  consumedToTtc: number;
  creditTotalTtc: number;
  appliedHtBeforeDiscount: number;
  appliedDiscount: number;
  appliedHt: number;
  appliedDocumentDiscount: number;
  appliedNetHt: number;
  appliedVat: number;
  appliedTtc: number;
  appliedBenefit: number | null;
  reversedAt: string | null;
  createdAt: string;
  lines: SalesCreditApplicationLine[];
}

/**
 * Projection minimale des avoirs rattaches a des BL/factures visibles.
 *
 * Elle ne donne pas acces au document avoir complet : elle expose seulement
 * les snapshots necessaires a l'application automatique, aux indicateurs de
 * filiation et a l'impression du compagnon legal.
 */
export interface SalesCreditCompanions {
  creditNotes: SalesDocument[];
  applications: SalesCreditApplication[];
}

export type SalesCreditApplicationStatus =
  | 'not_applied'
  | 'partially_applied'
  | 'fully_applied'
  | 'cancelled';

export interface CancelSalesCreditNoteInput {
  creditNoteId: string;
  reason?: string | null;
}

export interface SalesCreditCancellation {
  creditNoteId: string;
  cancelledAt: string;
  cancelledBy: string;
  reason: string | null;
  affectedArticleIds: string[];
}

/**
 * Référence structurelle least-privilege d'un avoir compagnon. Les montants
 * restent nullable : masquer une colonne financière ne doit ni cacher le lien
 * légal, ni transformer une valeur inconnue en zéro.
 */
export interface SalesCreditCompanionReference {
  id: string;
  docType: 'credit_note';
  number: string;
  docDate: string | null;
  parentDocId: string | null;
  parentDocIds: string[];
  /** Documents du scope explicite auxquels la projection a rattaché l'avoir. */
  linkedDocumentIds: string[];
  totalTtc: number | null;
}

/** Agrégat d'utilisation d'un avoir, sans identifiant de facture divulgué. */
export interface SalesCreditApplicationStatusSnapshot {
  creditNoteId: string;
  totalTtc: number | null;
  invoiceAppliedTtc: number | null;
  paymentAppliedTtc: number | null;
  appliedTtc: number | null;
  remainingTtc: number | null;
  status: SalesCreditApplicationStatus;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
}

export interface CreateSalesCreditApplicationLineInput {
  creditNoteLineId: string;
  sourceLineId?: string | null;
  lineNumber: number;
  articleId?: string | null;
  description: string;
  appliedQuantity: number;
  unitPriceHt: number;
  unitPriceTtc?: number | null;
  vatRate: number;
  appliedHtBeforeDiscount: number;
  appliedDiscount: number;
  appliedHt: number;
  appliedDocumentDiscount: number;
  appliedNetHt: number;
  appliedVat: number;
  appliedTtc: number;
  appliedBenefit?: number;
}

export interface CreateSalesCreditApplicationInput {
  creditNoteId: string;
  sourceDeliveryId: string;
  creditTotalTtc: number;
  consumedFromTtc: number;
  appliedHtBeforeDiscount: number;
  appliedDiscount: number;
  appliedHt: number;
  appliedDocumentDiscount: number;
  appliedNetHt: number;
  appliedVat: number;
  appliedTtc: number;
  appliedBenefit?: number;
  lines: CreateSalesCreditApplicationLineInput[];
}

/** Une autre facture a consommé tout ou partie du même avoir entre lecture et écriture. */
export class CreditNoteApplicationConflictError extends Error {
  readonly creditNoteId: string | null;
  constructor(creditNoteId: string | null = null) {
    super('credit note balance was already consumed by another invoice');
    this.name = 'CreditNoteApplicationConflictError';
    this.creditNoteId = creditNoteId;
  }
}

/** Les avoirs lies ont change ou leurs montants ne sont pas lisibles de facon
 * suffisamment complete pour calculer/imprimer une facture nette. */
export class SalesCreditCompanionsUnavailableError extends Error {
  constructor() {
    super('linked sales credit notes changed or are unavailable');
    this.name = 'SalesCreditCompanionsUnavailableError';
  }
}

/**
 * Un BL est devenu facturé avant la création de l'avoir. L'avoir doit alors
 * être créé depuis la facture, jamais rattaché après coup au BL.
 */
export class CreditNoteSourceInvoicedError extends Error {
  readonly sourceDocumentId: string;

  constructor(sourceDocumentId: string) {
    super('credit note source delivery is already invoiced');
    this.name = 'CreditNoteSourceInvoicedError';
    this.sourceDocumentId = sourceDocumentId;
  }
}

/** La quantite retournable a change entre la lecture et le submit. */
export class CreditNoteQuantityUnavailableError extends Error {
  constructor() {
    super('credit note return quantity is no longer available');
    this.name = 'CreditNoteQuantityUnavailableError';
  }
}

/** L'entrepot historique/plan stock ne peut plus etre prouve sans ambiguite. */
export class CreditNoteStockHistoryUnavailableError extends Error {
  constructor() {
    super('credit note stock history is unavailable');
    this.name = 'CreditNoteStockHistoryUnavailableError';
  }
}

/** Un avoir consommé, même partiellement, ne peut plus être annulé. */
export class CreditNoteCancellationConsumedError extends Error {
  constructor() {
    super('a consumed credit note cannot be cancelled');
    this.name = 'CreditNoteCancellationConsumedError';
  }
}

/** Le fait d'annulation existe déjà avec un autre motif immuable. */
export class CreditNoteCancellationConflictError extends Error {
  constructor() {
    super('credit note cancellation already exists with different facts');
    this.name = 'CreditNoteCancellationConflictError';
  }
}

/**
 * Un BL ou une facture porte deja un avoir en aval. Toute mutation de sa
 * source changerait retroactivement les quantites/montants qui ont fonde cet
 * avoir ; toute suppression detruirait sa filiation ou son application.
 */
export type SalesDocumentCreditLockReason =
  | 'credit_note_child'
  | 'credit_note_line_reference'
  | 'credit_application';

export class SalesDocumentCreditLockedError extends Error {
  readonly documentId: string;
  readonly action: 'update' | 'delete';
  readonly reason: SalesDocumentCreditLockReason;

  constructor(
    documentId: string,
    action: 'update' | 'delete',
    reason: SalesDocumentCreditLockReason,
  ) {
    super(`sales document is locked by a credit note (${action}: ${reason})`);
    this.name = 'SalesDocumentCreditLockedError';
    this.documentId = documentId;
    this.action = action;
    this.reason = reason;
  }
}

/**
 * Details of a document-number collision, raised when an offline-proposed (or
 * explicitly provided) number was consumed by another device before this
 * client synced. The sync layer parks the entry and asks the USER how to
 * renumber — nothing is decided silently.
 */
export interface DocumentNumberConflictDetails {
  docType: SalesDocType;
  proposedNumber: string;
  nextFreeNumber: string;
  nextFreeValue: number;
  prefix: string;
  padding: number;
  /** null quand le tenant numérote sans année ({PREFIX}-{NNNN}). */
  year: number | null;
}

export class DocumentNumberConflictError extends Error {
  readonly details: DocumentNumberConflictDetails;
  constructor(details: DocumentNumberConflictDetails) {
    super(
      `document number ${details.proposedNumber} already consumed — next free is ${details.nextFreeNumber}`,
    );
    this.name = 'DocumentNumberConflictError';
    this.details = details;
  }
}

/**
 * Une facture serveur qui couvre déjà un des documents sources qu'on voulait
 * facturer. Projection légère : de quoi identifier le document dans le dialogue
 * d'arbitrage, sans rapatrier ses lignes.
 */
export interface ConflictingInvoiceSummary {
  id: string;
  number: string;
  docDate: string;
  totalTtc: number;
  /** Tous les parents couverts par cette facture (pas seulement les miens). */
  parentDocIds: string[];
  /** Numéros de ces parents, pour un affichage lisible. */
  parentNumbers: string[];
}

/**
 * Un document source (BL / commande / bon de réception) est déjà facturé par un
 * AUTRE document. Levé par createDocument avant de créer une seconde facture
 * pour la même livraison — ce que rien n'empêchait jusqu'ici, ni hors-ligne ni
 * entre deux postes connectés.
 *
 * Ce n'est pas une erreur technique mais un arbitrage : la couche sync park
 * l'entrée en `needs_input` et l'utilisateur choisit quelle facture garder.
 */
export interface ParentAlreadyInvoicedDetails {
  scope: 'sales' | 'purchases';
  /** Type du document qu'on essayait de créer ('invoice'). */
  docType: string;
  /** Numéro provisoire/proposé de MA facture, quand il y en a un. */
  ownNumber: string | null;
  ownDocDate: string;
  ownTotalTtc: number;
  /** Sources que MA facture prétend couvrir (numéros lisibles). */
  ownParentNumbers: string[];
  /** Sources déjà prises, avec la facture serveur qui les couvre. */
  takenParents: Array<{
    parentId: string;
    parentNumber: string;
    invoiceId: string;
  }>;
  /** Factures serveur en cause, dédoublonnées. */
  invoices: ConflictingInvoiceSummary[];
}

export class ParentAlreadyInvoicedError extends Error {
  readonly details: ParentAlreadyInvoicedDetails;
  constructor(details: ParentAlreadyInvoicedDetails) {
    super(
      `documents already invoiced: ${details.takenParents
        .map((p) => p.parentNumber)
        .join(
          ', ',
        )} — covered by ${details.invoices.map((i) => i.number).join(', ')}`,
    );
    this.name = 'ParentAlreadyInvoicedError';
    this.details = details;
  }
}

/**
 * Le document a changé côté serveur depuis la version que l'utilisateur avait
 * sous les yeux quand il l'a modifié hors-ligne.
 *
 * Sans ce contrôle, le rejeu écrasait purement et simplement le travail de
 * l'autre poste, sans un mot. Le contrôle n'est actif que si l'appelant fournit
 * `baseUpdatedAt` — c'est-à-dire uniquement sur le chemin de rejeu hors-ligne,
 * là où la fenêtre se compte en heures. La saisie en ligne garde le
 * dernier-écrit-gagne (l'y appliquer ferait de faux conflits sur deux
 * enregistrements successifs du même utilisateur).
 */
export interface ConcurrentEditDetails {
  scope: 'sales' | 'purchases';
  documentId: string;
  docType: string;
  number: string;
  /** Version que l'utilisateur éditait. */
  baseUpdatedAt: string;
  /** Version actuelle du serveur. */
  serverUpdatedAt: string;
  /** Totaux des deux versions, pour rendre l'écart lisible. */
  mineTotalTtc: number | null;
  serverTotalTtc: number;
  mineLineCount: number | null;
  serverLineCount: number;
}

export class ConcurrentEditError extends Error {
  readonly details: ConcurrentEditDetails;
  constructor(details: ConcurrentEditDetails) {
    super(
      `document ${details.number} was modified elsewhere (base ${details.baseUpdatedAt}, server ${details.serverUpdatedAt})`,
    );
    this.name = 'ConcurrentEditError';
    this.details = details;
  }
}

/**
 * Un document d'achat portant ce numéro existe déjà pour ce fournisseur.
 *
 * Les documents d'achat n'ont pas de séquence : ils portent le numéro DU
 * FOURNISSEUR, unique par fournisseur. Un doublon au rejeu veut presque
 * toujours dire que la MÊME facture a été saisie deux fois — une fois
 * hors-ligne, une fois sur un autre poste. On ne peut pas le deviner à sa
 * place : c'est un arbitrage.
 */
export interface PurchaseNumberTakenDetails {
  docType: string;
  number: string;
  supplierId: string;
  supplierName: string | null;
  /** Ce que j'essayais de créer. */
  mineDocDate: string;
  mineTotalTtc: number;
  mineLineCount: number;
  /** Le document déjà en place côté serveur. */
  existing: {
    id: string;
    docType: string;
    docDate: string;
    totalTtc: number;
    isInvoiced: boolean;
  };
}

export class PurchaseNumberTakenError extends Error {
  readonly details: PurchaseNumberTakenDetails;
  constructor(details: PurchaseNumberTakenDetails) {
    super(
      `purchase document number ${details.number} already used for this supplier`,
    );
    this.name = 'PurchaseNumberTakenError';
    this.details = details;
  }
}

/**
 * Une commande (boutique ou Espace Pro) ne peut pas être supprimée : son bon de
 * livraison est déjà engagé en aval et refuse de partir avec elle.
 *
 * Supprimer la commande en laissant le BL derrière produirait exactement ce que
 * la règle « commande annulée ⇒ BL retiré » interdit : du stock sorti et un CA
 * comptabilisé pour une commande qui n'existe plus, sans plus aucun document
 * pour expliquer l'écart. On refuse donc l'opération entière, et on nomme le
 * document fautif pour que l'utilisateur sache quoi traiter d'abord.
 */
export interface OrderDeletionBlockedDetails {
  scope: 'storefront' | 'pro';
  /** Référence lisible de la commande : numéro boutique ou code `CMD-…`. */
  orderRef: string;
  /** Numéro du BL qui bloque, quand il a pu être lu. */
  deliveryNumber: string | null;
  reason: DeliveryRemovalBlocker;
}

export class OrderDeletionBlockedError extends Error {
  readonly details: OrderDeletionBlockedDetails;
  constructor(details: OrderDeletionBlockedDetails) {
    super(
      `order ${details.orderRef} cannot be deleted: linked delivery ${
        details.deliveryNumber ?? '?'
      } is ${details.reason}`,
    );
    this.name = 'OrderDeletionBlockedError';
    this.details = details;
  }
}

export interface CreateSalesDocumentInput {
  docType: SalesDocType;
  /** Migration-only: preserve the source document number instead of consuming the next counter. */
  number?: string;
  /**
   * Offline-first: the provisional number this client minted while
   * disconnected (continuing the last known sequence). At replay the server
   * atomically claims it — kept when still free, otherwise the create fails
   * with DocumentNumberConflictError and the sync layer asks the user.
   * Ignored when `number` is set.
   */
  proposedNumber?: string;
  /** Idempotency key. When set, a retry with the same key returns the already-
   *  created document instead of minting a duplicate (handles a create that
   *  succeeded server-side but timed out client-side). */
  clientRequestId?: string;
  /**
   * POS-only composite: persists this delivery, stock effects and its receipt
   * in one server transaction under `clientRequestId`.
   */
  posCheckout?: {
    payment: Omit<
      CreatePaymentInput,
      'allocations' | 'clientRequestId' | 'atomicRequestKind' | 'cashSessionId'
    >;
    cashSessionId: string | null;
  };
  /**
   * Standard BL form checkout: persists the delivery, stock effects, receipt,
   * allocation and optional cash movement in one database transaction. Unlike
   * `posCheckout`, this accepts normal negotiated prices and partial payment.
   */
  deliveryPayment?: {
    payment: Omit<
      CreatePaymentInput,
      'allocations' | 'clientRequestId' | 'atomicRequestKind' | 'cashSessionId'
    >;
    /** Explicit drawer, or omitted to apply the configured auto-attach rule. */
    cashSessionId?: string | null;
  };
  /** Explicit invoice validation: invoice and selected receipt commit together. */
  invoicePayment?: {
    payment: Omit<CreatePaymentInput, 'allocations' | 'clientRequestId' | 'atomicRequestKind' | 'cashSessionId'>;
    cashSessionId?: string | null;
  };
  /**
   * Import historique privilegie uniquement. Autorise le RPC dedie a
   * reconstituer une facture/un avoir ancien sans inventer des reservations de
   * stock ou applications modernes. Ne doit jamais etre pose par un formulaire.
   */
  historicalImport?: true;
  /** Opaque server-issued supervisor capability. Used only when the maximum
   *  requested discount exceeds the current role ceiling. */
  supervisorToken?: string;
  clientId?: string | null;
  /** Commercial origin. Keep null/undefined for central online orders. */
  originStoreId?: string | null;
  /** Origin of the document — 'local' (default) | 'messenger' | 'instagram' | 'whatsapp' | ... */
  channel?: string | null;
  customerSource?: SalesCustomerSource;
  onlineCustomerId?: string | null;
  /**
   * Statut boutique posé DÈS la création. Nécessaire pour une commande
   * boutique saisie à la main : sans lui la commande naît sans statut et
   * n'apparaît dans aucun filtre de `/storefront/orders`.
   */
  storefrontOrderStatus?:
    | import('./storefront-types').StorefrontOrderStatus
    | null;
  proCustomerId?: string | null;
  proClientId?: string | null;
  representativeId?: string | null;
  carRegistrationNumber?: string | null;
  docDate: string;
  validityDate?: string;
  parentDocId?: string;
  parentDocIds?: string[];
  notes?: string;
  customProperties?: DocumentPropertyValues;
  lines: CreateSalesLineInput[];
  /** Avoirs de BL à réserver en même temps que cette facture. */
  creditApplications?: CreateSalesCreditApplicationInput[];
  /**
   * Retours stock à persister dans la MÊME transaction que l'avoir. Les
   * `sourceLineId` désignent les lignes source portées par `lines`; l'adapter
   * les remplace par les UUID préalloués des nouvelles lignes d'avoir.
   */
  stockReturns?: CreateSalesCreditStockReturnInput[];
  /**
   * Warehouse used by the mandatory atomic delivery + stock command. It is
   * required when at least one article line is tracked (or has an unknown
   * legacy tracking state); `null`/`undefined` is valid for free-text and
   * explicitly untracked lines. The atomic server command re-checks this hint.
   */
  stockWarehouseId?: string | null;
  /**
   * Caisse (tiroir) d'origine d'un ticket POS. Le serveur vérifie qu'elle est
   * active, accessible et rattachée au magasin d'origine du document.
   */
  cashRegisterId?: string | null;
  /**
   * Import/migration UNIQUEMENT : conserver les coûts de ligne fournis TELS
   * QUELS (0/absent = coût inconnu, la ligne restera exclue du bénéfice).
   * Sans ce drapeau, une ligne sans coût est complétée au coût net catalogue
   * DU JOUR — correct pour une vente créée aujourd'hui (gel au moment de la
   * vente), FAUX pour un document historique importé : le réimport Tunielec du
   * 2026-07-18 a ainsi figé le coût de 2026 sur des ventes de 2024-2025 et
   * fabriqué des bénéfices négatifs sur les vieux documents.
   */
  preserveLineCosts?: boolean;
  /**
   * Lève le garde-fou anti-double-facturation (voir ParentAlreadyInvoicedError).
   * Posé UNIQUEMENT par une décision explicite de l'utilisateur dans le dialogue
   * de conflits (« garder ma facture ») et par les imports/migrations, qui
   * rejouent une filiation historique déjà cohérente.
   */
  allowDuplicateParentInvoice?: boolean;
  /** Document exonéré de TVA — les lignes fournies doivent déjà être à 0 %
   *  (voir exemptLinesFromVat, @erp/domain/sales). */
  vatExempt?: boolean;
  // Totaux calculés par le domain avant envoi
  totalHt?: number;
  totalDiscount?: number;
  documentDiscountMode?: DiscountMode;
  documentDiscountValue?: number;
  documentDiscount?: number;
  totalVat?: number;
  totalTtc?: number;
  withholdingAmount?: number;
  stampAmount?: number;
  /** FODEC calculé par le domain (calculateDocumentTotals) ; omis = 0. */
  fodecRate?: number;
  fodecAmount?: number;
  deliveryFee?: number;
  proCommandeCode?: string | null;
  netToPay?: number;
}

export interface CreateSalesLineInput {
  /** Existing line identity on edit; creation always allocates fresh lines. */
  id?: string;
  articleId?: string;
  variantId?: string | null;
  variantLabel?: string | null;
  /** Libellé de l'unité de vente choisie (« Carton »…) — voir
   *  SalesDocumentLine.unitLabel. Omis/null = unité de base. */
  unitLabel?: string | null;
  /** Facteur de l'unité choisie (pièces de base par unité) — voir
   *  SalesDocumentLine.unitFactor. Omis = 1. Pilote le décompte de stock. */
  unitFactor?: number;
  description: string;
  quantity: number;
  unitPriceHt: number;
  /** Ancre TTC (prix unitaire TTC catalogue) — absente/null pour une ligne HT. */
  unitPriceTtc?: number | null;
  /**
   * Indice de catalogue utilisé uniquement par le garde client du BL. La base
   * revérifie toujours l'état réel ; absent signifie « suivi » (fail-closed).
   */
  stockTrackingEnabled?: boolean;
  purchasePrice?: number;
  discountPct?: number;
  discountMode?: DiscountMode;
  discountAmount?: number;
  vatRate: number;
  totalHt?: number;
  totalTtc?: number;
  /** Document d'origine de la ligne — voir SalesDocumentLine.sourceDocId.
   *  Posé par la facturation groupée : chaque ligne garde le BL dont elle vient. */
  sourceDocId?: string | null;
  /** Ligne d'origine retournée (lignes d'avoir uniquement). */
  sourceLineId?: string | null;
}

export interface CreateSalesCreditStockReturnSourceInput {
  sourceLineId: string;
  quantity: number;
}

export interface CreateSalesCreditStockReturnInput {
  articleId: string;
  variantId?: string | null;
  warehouseId: string;
  quantity: number;
  movementDate: string;
  notes?: string;
  sourceReturns: CreateSalesCreditStockReturnSourceInput[];
}

export type UpdateSalesDocumentInput = Partial<
  Omit<CreateSalesDocumentInput, 'docType'>
> & {
  /** Set the storefront lifecycle status. Ignored on local/POS/manual documents. */
  storefrontOrderStatus?:
    | import('./storefront-types').StorefrontOrderStatus
    | null;
  /**
   * `updated_at` de la version que l'utilisateur éditait. Quand il est fourni,
   * l'écriture est refusée par ConcurrentEditError si le serveur a bougé
   * depuis. Posé UNIQUEMENT par la file hors-ligne (voir ConcurrentEditDetails)
   * — jamais transmis à la base.
   */
  baseUpdatedAt?: string;
};

export type SortDirection = 'asc' | 'desc';

/**
 * Server-side ordering for a document list. `field` is a document column key
 * (e.g. 'number', 'docDate', 'totalTtc', 'isInvoiced'); the adapter maps it to
 * the real DB column. Unknown/computed fields fall back to the default order:
 * doc_date desc (latest document date first), then created_at desc.
 */
export interface DocumentSort {
  field: string;
  direction: SortDirection;
}

export interface SalesDocumentFilters {
  /**
   * Rafraîchissement DELTA du miroir hors-ligne : ne rend que les lignes dont
   * `updated_at` est STRICTEMENT postérieur à cet instant ISO.
   *
   * Le miroir desktop se re-paginait entièrement toutes les 30 minutes (~5 Mo
   * par cycle, 24 h/24) alors que quelques lignes seulement avaient bougé.
   * Un trigger `set_updated_at` maintient la colonne sur chaque UPDATE, donc
   * le delta est fiable.
   *
   * ⚠ Ne dit RIEN des lignes SUPPRIMÉES : sans tombstone, une suppression faite
   * depuis un autre poste reste invisible du delta. C'est pourquoi il ne
   * remplace pas la re-semence complète, il l'espace.
   */
  updatedSince?: string;
  docType?: SalesDocType;
  search?: string;
  /** Restrict to any of these clients. Takes precedence over `clientId`. */
  clientIds?: string[];
  clientId?: string;
  /** Restrict to one physical origin store. null selects company/online origin. */
  originStoreId?: string | null;
  /** With a physical originStoreId, also include centrally originated documents
   * whose origin is null. Explicit null and unscoped reads keep their semantics. */
  includeGlobalOrigin?: boolean;
  representativeId?: string;
  /** Restrict storefront customer history without downloading every order. */
  onlineCustomerId?: string;
  /** Storefront-origin orders only (including legacy rows identified by their
   * online customer rather than an explicit channel). */
  storefrontOnly?: boolean;
  /** Skip party enrichment for consumers that gate customer profiles behind a
   * separate feature. Document ids and frozen document fields remain intact. */
  includeCustomerEnrichment?: boolean;
  dateFrom?: string;
  dateTo?: string;
  isInvoiced?: boolean;
  paymentStatus?: 'paid' | 'unpaid' | 'partially_paid';
  /** Only documents containing at least one line for this article. Resolved
   *  server-side from the lines table so callers never have to fetch the whole
   *  document list. Not combinable with `paymentStatus`.
   *
   *  ⚠ Ne PAS y ajouter `includeLines` pour lire les lignes de l'article : ça
   *  rapatrie toutes les lignes des documents trouvés, celles des autres
   *  articles comprises. `listLinesByArticle` rend exactement les bonnes, en
   *  une requête indexée. */
  lineArticleId?: string;
  /** Only documents descending from one of these parents — the reverse of
   *  `parentDocIds`. Lets a list resolve, in ONE query, which of the rows it
   *  displays have already been transformed downstream. */
  parentDocIdIn?: string[];
  /**
   * Ces documents précisément, par id — pour un écran qui connaît déjà les ids
   * dont il a besoin (la filiation des lignes affichées) et ne peut pas
   * compter sur la page courante pour les contenir. Liste vide = aucun
   * document (jamais « tous »), comme `ArticleFilters.ids`.
   *
   * ⚠ Un seul `in.()` : les ids voyagent dans l'URL, donc à réserver aux
   * lots de l'ordre de la page affichée, pas à des milliers d'ids.
   */
  ids?: string[];
  includeLines?: boolean;
  offset?: number;
  limit?: number;
  sort?: DocumentSort;
}

/**
 * Une remise déjà accordée à un client sur un article, lue sur la ligne du
 * document qui la portait. Sert à proposer la même remise sur un nouveau
 * document (voir @erp/domain/sales — client-discount-history).
 *
 * Volontairement plat : c'est une PROJECTION de lecture (ligne + entête), pas
 * un SalesDocument amputé.
 */
export interface ClientArticleDiscount {
  documentId: string;
  documentNumber: string;
  docType: SalesDocType;
  /** Date du document, ISO `YYYY-MM-DD`. */
  docDate: string;
  quantity: number;
  unitPriceHt: number;
  unitPriceTtc: number | null;
  vatRate: number;
  discountMode: DiscountMode;
  discountPct: number;
  discountAmount: number;
}

export interface ClientArticleDiscountQuery {
  clientId: string;
  articleId: string;
  /** Plancher de date (ISO `YYYY-MM-DD`) : au-delà, une remise est trop
   *  ancienne pour être proposée. */
  since?: string;
  /** Nombre de documents remontés, les plus récents d'abord. Défaut : 10. */
  limit?: number;
}

// ---- Achats ----

export type PurchaseDocType = 'order' | 'reception' | 'invoice' | 'credit_note';
export type PurchaseStockPostingMode =
  | 'live'
  | 'historical_snapshot'
  | 'legacy_untracked';

export interface PurchaseDocument {
  id: string;
  docType: PurchaseDocType;
  number: string;
  supplierId: string;
  /**
   * Dépôt crédité par un bon de réception. `null` sur les autres types et sur
   * les BR historiques importés avant la persistance explicite du dépôt.
   */
  warehouseId?: string | null;
  /**
   * `live` garantit un stock atomiquement relié au BR. Les deux autres valeurs
   * rendent visibles les exceptions héritées/importées, sans les recréditer.
   */
  stockPostingMode?: PurchaseStockPostingMode;
  /** Clé stable d'une tentative de création, pour rendre un rejeu idempotent. */
  clientRequestId?: string | null;
  docDate: string;
  parentDocId: string | null;
  parentDocIds: string[];
  isInvoiced: boolean;
  /** Document exonéré de TVA — voir SalesDocument.vatExempt. */
  vatExempt?: boolean;
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  withholdingAmount: number;
  stampAmount: number;
  netToPay: number;
  notes: string | null;
  customProperties?: DocumentPropertyValues;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: PurchaseDocumentLine[];
  supplier?: Supplier;
}

export interface PurchaseDocumentLine {
  id: string;
  documentId: string;
  lineNumber: number;
  articleId: string | null;
  variantId?: string | null;
  variantLabel?: string | null;
  description: string;
  quantity: number;
  unitPriceHt: number;
  purchasePrice: number;
  discountPct: number;
  vatRate: number;
  totalHt: number;
  totalTtc: number;
  /** Document d'origine de la ligne — le bon de réception dont une facture
   *  d'achat groupée l'a reprise. Voir SalesDocumentLine.sourceDocId : même
   *  rôle, même absence de clé étrangère (mig 20260803110000). */
  sourceDocId?: string | null;
  /** Read-only enrichment: see SalesDocumentLine.articleCode. */
  articleCode?: string | null;
  articleName?: string | null;
  /** Read-only enrichment: see SalesDocumentLine.articleIsInvoiceable. */
  articleIsInvoiceable?: boolean | null;
  /** Unité figée lors de l'enregistrement de la ligne. */
  articleUnit?: string | null;
}

export interface CreatePurchaseDocumentInput {
  docType: PurchaseDocType;
  number: string;
  supplierId: string;
  /** Obligatoire pour un bon de réception opérationnel. */
  warehouseId?: string;
  /**
   * UUID stable d'une même tentative. Un rejeu rend le document déjà créé au
   * lieu de créer un second BR et une seconde entrée de stock.
   */
  clientRequestId?: string;
  /** Explicit invoice validation: invoice and supplier disbursement commit together. */
  invoicePayment?: {
    payment: Omit<CreatePaymentInput, 'allocations' | 'clientRequestId' | 'atomicRequestKind' | 'cashSessionId'>;
    cashSessionId?: string | null;
  };
  docDate: string;
  parentDocId?: string;
  parentDocIds?: string[];
  notes?: string;
  customProperties?: DocumentPropertyValues;
  lines: CreatePurchaseLineInput[];
  /** Voir CreateSalesDocumentInput.allowDuplicateParentInvoice. */
  allowDuplicateParentInvoice?: boolean;
  /** Document exonéré de TVA — lignes déjà à 0 % (voir exemptLinesFromVat). */
  vatExempt?: boolean;
  totalHt?: number;
  totalVat?: number;
  totalTtc?: number;
  withholdingAmount?: number;
  stampAmount?: number;
  netToPay?: number;
}

export interface CreatePurchaseLineInput {
  articleId?: string;
  variantId?: string | null;
  variantLabel?: string | null;
  description: string;
  quantity: number;
  /**
   * Unité catalogue figée et persistée sur la ligne. Pour un BR, l'adapter la
   * résout depuis l'article autorisé avant le calcul du stock : un appelant ne
   * peut ni l'omettre ni la falsifier pour contourner une unité indivisible.
   */
  unit?: string | null;
  unitPriceHt: number;
  discountPct?: number;
  vatRate: number;
  totalHt?: number;
  totalTtc?: number;
  /** Document d'origine de la ligne — voir PurchaseDocumentLine.sourceDocId.
   *  Posé par la facturation groupée : chaque ligne garde son BR. */
  sourceDocId?: string | null;
}

export type UpdatePurchaseDocumentInput = Partial<
  Omit<CreatePurchaseDocumentInput, 'docType'>
> & {
  /** Voir UpdateSalesDocumentInput.baseUpdatedAt. */
  baseUpdatedAt?: string;
};

export interface PurchaseDocumentFilters {
  /**
   * Rafraîchissement DELTA du miroir hors-ligne : ne rend que les lignes dont
   * `updated_at` est STRICTEMENT postérieur à cet instant ISO.
   *
   * Le miroir desktop se re-paginait entièrement toutes les 30 minutes (~5 Mo
   * par cycle, 24 h/24) alors que quelques lignes seulement avaient bougé.
   * Un trigger `set_updated_at` maintient la colonne sur chaque UPDATE, donc
   * le delta est fiable.
   *
   * ⚠ Ne dit RIEN des lignes SUPPRIMÉES : sans tombstone, une suppression faite
   * depuis un autre poste reste invisible du delta. C'est pourquoi il ne
   * remplace pas la re-semence complète, il l'espace.
   */
  updatedSince?: string;
  docType?: PurchaseDocType;
  search?: string;
  /** Restrict to any of these suppliers. Takes precedence over `supplierId`. */
  supplierIds?: string[];
  supplierId?: string;
  dateFrom?: string;
  dateTo?: string;
  isInvoiced?: boolean;
  /** Only documents containing at least one line for this article (resolved
   *  server-side from the lines table).
   *
   *  ⚠ Même mise en garde que côté ventes : pour les LIGNES de l'article,
   *  `listLinesByArticle`, pas `includeLines`. */
  lineArticleId?: string;
  /** Only documents descending from one of these parents — same contract as
   *  SalesDocumentFilters.parentDocIdIn. */
  parentDocIdIn?: string[];
  /** Ces documents précisément, par id — même contrat que
   *  `SalesDocumentFilters.ids` (liste vide = aucun document). */
  ids?: string[];
  /** Embed `lines` on each returned document. OFF by default: the joined
   *  lines+article payload multiplied list latency by ~5-10× (320-720 ms
   *  measured) for pages that only read document totals. Same contract as
   *  SalesDocumentFilters.includeLines; detail fetches (getDocumentById)
   *  always include lines. */
  includeLines?: boolean;
  offset?: number;
  limit?: number;
  sort?: DocumentSort;
}

// ---- Stores / branches ----

/** Operational location inside a tenant company. */
export interface Store {
  id: string;
  code: string;
  name: string;
  city: string | null;
  address: string | null;
  phone: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStoreInput {
  code: string;
  name: string;
  city?: string | null;
  address?: string | null;
  phone?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
}

export type UpdateStoreInput = Partial<CreateStoreInput>;

// ---- Stock ----

export interface Warehouse {
  id: string;
  storeId: string;
  code: string;
  name: string;
  isDefault: boolean;
}

export interface CreateWarehouseInput {
  /** Store that owns this physical stock location. Defaults to the tenant store. */
  storeId?: string;
  code: string;
  name: string;
  isDefault?: boolean;
}

export type UpdateWarehouseInput = Partial<CreateWarehouseInput>;

export type StockMovementType = 'in' | 'out' | 'adjustment';

export interface StockMovement {
  id: string;
  articleId: string;
  variantId?: string | null;
  warehouseId: string;
  movementType: StockMovementType;
  quantity: number;
  referenceType: string | null;
  referenceId: string | null;
  movementDate: string;
  notes: string | null;
  createdAt: string;
}

export interface CreateStockMovementInput {
  articleId: string;
  variantId?: string | null;
  warehouseId: string;
  movementType: StockMovementType;
  quantity: number;
  referenceType?: string;
  referenceId?: string;
  movementDate: string;
  notes?: string;
  /** Durable replay key for bounded mobile stock commands. */
  clientRequestId?: string;
  /** Ventilation autoritative d'un retour d'avoir par ligne d'avoir. */
  sourceReturns?: Array<{ creditNoteLineId: string; quantity: number }>;
}

export type StockTransferStatus = 'draft' | 'dispatched' | 'received';

export type StockAtomicRequestKind =
  | 'stock_adjustment'
  | 'inventory_create'
  | 'inventory_validate'
  | 'transfer_create'
  | 'transfer_dispatch'
  | 'transfer_receive';

export interface StockTransferLine {
  id: string;
  transferId: string;
  lineNumber: number;
  articleId: string;
  requestedQuantity: number;
  receivedQuantity: number | null;
}

export interface StockTransferDocument {
  id: string;
  number: string;
  originWarehouseId: string;
  destinationWarehouseId: string;
  status: StockTransferStatus;
  transferDate: string;
  notes: string | null;
  responsibleUserId?: string | null;
  customProperties?: DocumentPropertyValues;
  dispatchedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: StockTransferLine[];
}

export interface CreateStockTransferInput {
  clientRequestId?: string;
  originWarehouseId: string;
  destinationWarehouseId: string;
  transferDate: string;
  notes?: string;
  responsibleUserId?: string | null;
  customProperties?: DocumentPropertyValues;
  lines: { articleId: string; quantity: number }[];
}

export interface ReceiveStockTransferInput {
  id: string;
  clientRequestId?: string;
  lines: { lineId: string; receivedQuantity: number }[];
}

export interface ArticleStock {
  articleId: string;
  warehouseId: string;
  quantity: number;
}

export interface Inventory {
  id: string;
  number: string;
  warehouseId: string;
  inventoryDate: string;
  isValidated: boolean;
  notes: string | null;
  createdAt: string;
  lines?: InventoryLine[];
}

export interface InventoryLine {
  id: string;
  inventoryId: string;
  articleId: string;
  theoreticalQty: number;
  countedQty: number;
  difference: number;
}

export interface CreateInventoryInput {
  clientRequestId?: string;
  warehouseId: string;
  inventoryDate: string;
  notes?: string;
  lines: { articleId: string; countedQty: number }[];
}

export type StockVehicleDocumentType = 'exit' | 'entry';

export interface StockExitDocument {
  id: string;
  storeId: string;
  /** Original ledger warehouse; absent on stock-neutral historical documents. */
  warehouseId?: string | null;
  /** Persisted effect provenance, never inferred from the current tenant policy. */
  stockDeducted?: boolean;
  documentType: StockVehicleDocumentType;
  number: string;
  /** Empty string when no vehicle registration was provided. */
  carRegistrationNumber: string;
  exitDate: string;
  parentDocumentId: string | null;
  notes: string | null;
  customProperties?: DocumentPropertyValues;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: StockExitDocumentLine[];
}

export interface StockExitDocumentLine {
  id: string;
  documentId: string;
  lineNumber: number;
  articleId: string;
  variantId?: string | null;
  variantLabel?: string | null;
  quantity: number;
  notes: string | null;
}

export interface CreateStockExitDocumentInput {
  storeId?: string;
  warehouseId?: string | null;
  /** Stable identity of one explicit submission, reused after unknown outcomes. */
  clientRequestId?: string;
  documentType?: StockVehicleDocumentType;
  carRegistrationNumber?: string;
  exitDate: string;
  parentDocumentId?: string | null;
  notes?: string;
  customProperties?: DocumentPropertyValues;
  lines: CreateStockExitDocumentLineInput[];
}

export interface CreateStockExitDocumentLineInput {
  articleId: string;
  variantId?: string | null;
  variantLabel?: string | null;
  quantity: number;
  notes?: string;
}

// ---- Paiements ----

export type PaymentType = 'receipt' | 'disbursement';
export type PaymentMethod = 'cash' | 'transfer' | 'check' | 'draft' | 'card';
/**
 * 'client' = a client; 'supplier' = a supplier; 'other' = a generic expense/income
 * category (payroll, rent, utilities…) referencing `payment_categories` via
 * `thirdPartyId`.
 */
export type PaymentThirdPartyType = 'client' | 'supplier' | 'other';

export interface Payment {
  id: string;
  number: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  thirdPartyType: PaymentThirdPartyType;
  thirdPartyId: string;
  /** Store that collected or disbursed the money, independent from document origin. */
  collectionStoreId: string | null;
  bankAccountId: string | null;
  cashSessionId: string | null;
  amount: number;
  paymentDate: string;
  reference: string | null;
  notes: string | null;
  /** Set when the payment was auto-generated from a recurring rule. */
  recurringPaymentId: string | null;
  createdAt: string;
  allocations?: PaymentAllocation[];
}

export interface PaymentAllocation {
  id: string;
  paymentId: string;
  documentType: string;
  documentId: string;
  /**
   * Signed document application. Normally positive; a sales or purchase
   * credit note applied to an invoice is negative so the allocation sum
   * remains equal to the net cash receipt/disbursement.
   */
  allocatedAmount: number;
}

// ---- Lignes « balance » allégées ----
// Projections minimales pour les soldes client/fournisseur et les widgets
// d'encours : ces écrans tiraient l'INTÉGRALITÉ de sales_documents /
// purchase_documents / payments (lignes larges) pour n'en lire que 4-5
// champs. Servies par listBalanceRows() (paginé au-delà du cap PostgREST),
// le calcul du solde reste dans libs/domain.
export interface DocumentBalanceRow {
  id: string;
  /** clientId côté ventes, supplierId côté achats. */
  thirdPartyId: string;
  /** `delivery` côté ventes uniquement, et seulement NON facturé : un BL
   *  facturé est représenté par sa facture (même règle que le portefeuille). */
  docType: 'invoice' | 'credit_note' | 'delivery';
  isInvoiced?: boolean;
  totalTtc: number;
  netToPay: number;
  /** Timbre fiscal — tolérance de règlement (settlementToleranceFor) : sans
   *  lui, l'encours du tableau de bord compterait 1 DT sur chaque facture
   *  soldée que le reste de l'app affiche « Payée ». */
  stampAmount: number;
  /** Part de cet avoir déjà intégrée à une facture dont le total est net. */
  invoiceAppliedTtc?: number;
}

export interface PaymentBalanceRow {
  id: string;
  thirdPartyId: string;
  amount: number;
  paymentType: PaymentType;
  allocations: Array<{ documentId: string; allocatedAmount: number }>;
}

export interface CreatePaymentInput {
  /** Explicit durable ledger boundary; never inferred from allocation shape. */
  atomicRequestKind?: 'sales_invoice_collection' | 'payment';
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  thirdPartyType: PaymentThirdPartyType;
  thirdPartyId: string;
  collectionStoreId?: string | null;
  bankAccountId?: string;
  /**
   * Explicit drawer chosen for this durable command. When omitted, the
   * adapter resolves the current open session once for backward compatibility;
   * persisted callers should retain and replay the resolved value.
   */
  cashSessionId?: string | null;
  amount: number;
  paymentDate: string;
  reference?: string;
  notes?: string;
  recurringPaymentId?: string;
  /**
   * Occurrence this payment settles. Carries the anti-double-post guarantee:
   * a partial UNIQUE index in Postgres refuses a second payment for the same
   * occurrence, whatever the client does.
   */
  recurringOccurrenceId?: string;
  /**
   * One-time private claim returned by the recurring-occurrence command. It
   * binds the transient `posting` row to this exact payment payload and is
   * consumed atomically when the occurrence becomes `posted`.
   */
  recurringClaimToken?: string;
  /**
   * Idempotency key for an atomic direct-invoice collection. The adapter
   * rejects unsupported payload shapes instead of silently falling back to
   * the legacy multi-request writer.
   */
  clientRequestId?: string;
  /**
   * Optional linked traite persisted in the same atomic command and request
   * ledger as the payment. Mobile draft flows must use this field instead of
   * issuing a second repository mutation after the payment commits.
   */
  linkedDraft?: Omit<CreateCheckDraftInput, 'paymentId'>;
  /** See PaymentAllocation.allocatedAmount for credit-note sign semantics. */
  allocations?: {
    documentType: string;
    documentId: string;
    allocatedAmount: number;
  }[];
}

export interface UpdatePaymentInput {
  /** Durable idempotency key for the complete payment update intent. */
  clientRequestId?: string;
  paymentMethod?: PaymentMethod;
  collectionStoreId?: string | null;
  /** null efface le compte (ex. passage traite → espèces sans tiroir). */
  bankAccountId?: string | null;
  amount?: number;
  paymentDate?: string;
  reference?: string | null;
  notes?: string | null;
  /**
   * Sémantique de REMPLACEMENT : la liste fournie devient l'ensemble des
   * affectations du paiement — les lignes existantes absentes de la liste
   * sont supprimées. Rapprochement par `id` si fourni, sinon par
   * (documentType, documentId). Omettre le champ pour ne rien toucher.
   */
  allocations?: {
    id?: string;
    documentType: string;
    documentId: string;
    allocatedAmount: number;
  }[];
}

export interface PaymentFilters {
  paymentType?: PaymentType;
  thirdPartyType?: PaymentThirdPartyType;
  thirdPartyId?: string;
  collectionStoreId?: string | null;
  dateFrom?: string;
  dateTo?: string;
  documentId?: string;
  documentType?: string;
  recurringPaymentId?: string;
  /** Encaissements rattachés à ces sessions de caisse (une ou plusieurs). */
  cashSessionIds?: string[];
}

// ---- Catégories de paiement (référentiel pour third_party_type='other') ----

export interface PaymentCategory {
  id: string;
  code: string;
  name: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentCategoryInput {
  code: string;
  name: string;
  displayOrder?: number;
  isActive?: boolean;
}

export type UpdatePaymentCategoryInput = Partial<CreatePaymentCategoryInput>;

// ---- Paiements récurrents ----

export type RecurrenceFrequency = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface RecurringPayment {
  id: string;
  label: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  thirdPartyType: PaymentThirdPartyType;
  thirdPartyId: string;
  bankAccountId: string | null;
  amount: number;
  notes: string | null;

  frequency: RecurrenceFrequency;
  /** Multiplier on frequency unit (1 = every period, 3 = every third period). */
  intervalCount: number;
  /** 1..31 for monthly/quarterly/yearly; null = use day from start_date. Ignored for weekly. */
  dayOfMonth: number | null;
  startDate: string;
  endDate: string | null;
  nextOccurrenceDate: string;
  lastGeneratedDate: string | null;

  isActive: boolean;
  /**
   * Legacy mirror of `approvalMode` (true ⇔ 'automatic'). Kept in sync by the
   * adapter so already-installed mobile builds keep working; new code should
   * read `approvalMode`.
   * @deprecated use {@link RecurringPayment.approvalMode}
   */
  autoGenerate: boolean;
  /**
   * 'automatic' — each occurrence is booked on its due date, hands off.
   * 'manual'    — each occurrence waits in the approval inbox.
   */
  approvalMode: RecurringApprovalMode;

  createdAt: string;
  updatedAt: string;
}

export type RecurringApprovalMode = 'automatic' | 'manual';

/**
 * Lifecycle of one materialized due date.
 * `posting` is a transient lock: the device that flips pending → posting owns
 * the right to create the payment, so two devices can never book it twice.
 */
export type RecurringOccurrenceStatus =
  | 'pending'
  | 'posting'
  | 'posted'
  | 'skipped'
  | 'rejected';

/**
 * One due date of a recurring rule, with its own decision and its own snapshot
 * of the rule (editing the rule never rewrites an occurrence already decided).
 */
export interface RecurringOccurrence {
  id: string;
  recurringPaymentId: string;
  /** Date produced by the cadence. Immutable — uniqueness key and cursor anchor. */
  scheduledDate: string;
  /** Effective date. Only "reschedule" moves it. */
  dueDate: string;
  /** Original due date, set the first time the occurrence is rescheduled. */
  rescheduledFrom: string | null;

  amount: number;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  thirdPartyType: PaymentThirdPartyType;
  thirdPartyId: string | null;
  bankAccountId: string | null;
  notes: string | null;

  status: RecurringOccurrenceStatus;
  /** Whether the posting was done by the roll-forward or by a human. */
  postedVia: RecurringApprovalMode | null;
  paymentId: string | null;

  decisionNote: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  /** Why the last automatic posting attempt failed; the occurrence stays pending. */
  lastError: string | null;

  createdAt: string;
  updatedAt: string;

  /** Label of the owning rule, joined for display. */
  ruleLabel: string | null;
  /** Approval mode of the owning rule, joined so the inbox can label the source. */
  ruleApprovalMode: RecurringApprovalMode | null;
}

export interface RecurringOccurrenceFilters {
  status?: RecurringOccurrenceStatus | RecurringOccurrenceStatus[];
  recurringPaymentId?: string;
  dueFrom?: string;
  dueTo?: string;
  limit?: number;
}

export interface ApproveOccurrenceInput {
  /** Value date of the payment. Defaults to the occurrence due date. */
  paymentDate?: string;
  /** Override the snapshot amount (this month's bill came in higher). */
  amount?: number;
  reference?: string;
  note?: string;
}

export interface RescheduleOccurrenceInput {
  dueDate: string;
  note?: string;
}

export interface DeclineOccurrenceInput {
  note?: string;
}

/** Outcome of one roll-forward pass (materialize + post what is automatic). */
export interface RunScheduleResult {
  /** Occurrences newly written to the calendar. */
  materialized: number;
  /** Occurrences turned into payments without human input. */
  autoPosted: number;
  /** Occurrences still awaiting a decision once the pass finished. */
  pending: number;
  /** Rules whose backlog hit the per-pass cap — they resume on the next run. */
  truncatedRuleIds: string[];
  /** Auto-post failures. The occurrence stays pending and shows the reason. */
  failures: { occurrenceId: string; ruleLabel: string; message: string }[];
}

export interface CreateRecurringPaymentInput {
  label: string;
  paymentType: PaymentType;
  paymentMethod: PaymentMethod;
  thirdPartyType: PaymentThirdPartyType;
  thirdPartyId: string;
  bankAccountId?: string | null;
  amount: number;
  notes?: string;

  frequency: RecurrenceFrequency;
  intervalCount?: number;
  dayOfMonth?: number | null;
  startDate: string;
  endDate?: string | null;
  /** Optional override; defaults to startDate. */
  nextOccurrenceDate?: string;

  isActive?: boolean;
  /** @deprecated pass `approvalMode`; kept so old callers still compile. */
  autoGenerate?: boolean;
  approvalMode?: RecurringApprovalMode;
}

export type UpdateRecurringPaymentInput =
  Partial<CreateRecurringPaymentInput> & {
    /** Used after generating an occurrence to advance the cursor. */
    nextOccurrenceDate?: string;
    lastGeneratedDate?: string | null;
  };

export interface RecurringPaymentFilters {
  isActive?: boolean;
  paymentType?: PaymentType;
  thirdPartyType?: PaymentThirdPartyType;
  search?: string;
}

/** Result returned from running the "generate due payments" pass. */
export interface GenerateDueResult {
  generated: number;
  rulesProcessed: number;
}

// ---- Trésorerie ----

export type BankAccountType = 'cash' | 'bank';

export interface BankAccountFilters {
  storeId?: string | null;
  accountType?: BankAccountType;
  activeOnly?: boolean;
}

export interface CashRegisterOption {
  id: string;
  name: string;
  storeId: string;
  isDefaultCashRegister: boolean;
  /** Stock location debited by this register's sales; null = store default. */
  warehouseId?: string | null;
}

export interface DocumentAccessSettings {
  showDocumentCreatorToTeam: boolean;
  /** Server-resolved visibility for this caller; never a client preference. */
  canViewDocumentCreator: boolean;
}

export interface DocumentCreator {
  documentId: string;
  userId: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface BankAccount {
  id: string;
  /** Owning store for cash drawers; null for company-wide bank accounts. */
  storeId: string | null;
  name: string;
  bankName: string;
  bankId: string | null;
  rib: string | null;
  /** Adresse de l'agence — imprimée sous le nom de la banque sur la traite. */
  address: string | null;
  currentBalance: number;
  isActive: boolean;
  accountType: BankAccountType;
  /**
   * Tiroir cible des encaissements espèces saisis HORS écran POS.
   * Un seul par store (index partiel `uq_bank_accounts_default_cash_register`).
   */
  isDefaultCashRegister: boolean;
  /**
   * Entrepôt que les ventes de cette caisse décomptent. `null` = suit
   * l'entrepôt par défaut du magasin (comportement historique). Optionnel
   * parce que les caches persistés d'avant son ajout n'en portent pas.
   */
  warehouseId?: string | null;
  /** Null preserves normal store access; [] restricts to tenant owner/admin. */
  allowedUserIds?: string[] | null;
  createdAt: string;
}

export interface CreateBankAccountInput {
  /** Store owning a cash drawer; omit/null for company-wide bank accounts. */
  storeId?: string | null;
  name: string;
  bankName: string;
  bankId?: string | null;
  rib?: string;
  address?: string;
  currentBalance?: number;
  isActive?: boolean;
  accountType?: BankAccountType;
  isDefaultCashRegister?: boolean;
  /** Cash drawers only; must belong to the drawer's store (composite FK). */
  warehouseId?: string | null;
  /** Only the current tenant owner/admin can change a drawer's allowlist. */
  allowedUserIds?: string[] | null;
}

export type UpdateBankAccountInput = Partial<
  CreateBankAccountInput & { bankId: string | null }
>;

// ---- Banques tunisiennes (référentiel BCT) ----

export interface TnBank {
  id: string;
  code: string;
  shortName: string;
  fullName: string;
  fullNameAr: string | null;
  swiftBic: string | null;
  website: string | null;
  isIslamic: boolean;
  color: string | null;
  logoUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
}

/** BankAccount avec jointure tn_banks */
export interface BankAccountWithBank extends BankAccount {
  bankId: string | null;
  bank: TnBank | null;
}

// ---- Transactions bancaires ----

export type BankTransactionType =
  | 'payment'
  | 'manual'
  | 'fee'
  | 'transfer_in'
  | 'transfer_out'
  | 'interest'
  | 'check'
  | 'draft'
  | 'other';

export type BankTransactionDirection = 'in' | 'out';

export interface BankTransaction {
  id: string;
  bankAccountId: string;
  transactionDate: string;
  valueDate: string | null;
  direction: BankTransactionDirection;
  amount: number;
  transactionType: BankTransactionType;
  reference: string | null;
  description: string | null;
  paymentId: string | null;
  checkDraftId: string | null;
  counterpartyAccountId: string | null;
  isReconciled: boolean;
  reconciledAt: string | null;
  reconciledBy: string | null;
  statementLineId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBankTransactionInput {
  bankAccountId: string;
  transactionDate: string;
  valueDate?: string;
  direction: BankTransactionDirection;
  amount: number;
  transactionType: BankTransactionType;
  reference?: string;
  description?: string;
  paymentId?: string;
  checkDraftId?: string;
  counterpartyAccountId?: string;
  statementLineId?: string;
}

export type UpdateBankTransactionInput = Partial<
  Omit<CreateBankTransactionInput, 'bankAccountId'>
>;

export interface BankTransactionFilters {
  accountId?: string;
  dateFrom?: string;
  dateTo?: string;
  transactionType?: BankTransactionType;
  direction?: BankTransactionDirection;
  isReconciled?: boolean;
  search?: string;
}

// ---- Chèques & Traites ----

export type CheckDraftType = 'check' | 'draft';
export type CheckDraftDirection = 'received' | 'issued';
export type CheckDraftState =
  | 'in_wallet' // received: in hand, not yet submitted
  | 'submitted' // received: submitted to bank
  | 'cashed' // received: encaissé
  | 'bounced' // received: impayé
  | 'issued' // issued: written, not presented
  | 'accepted' // issued traite only: drawee accepted
  | 'presented' // issued: presented to bank
  | 'debited' // issued: bank debited
  | 'rejected'; // issued: refused

export interface CheckDraft {
  id: string;
  type: CheckDraftType;
  direction: CheckDraftDirection;
  number: string;
  /** Free-text bank name (payer's bank for received, fallback for issued) */
  bankName: string;
  /** FK to our own bank_accounts (required for issued direction) */
  bankAccountId: string | null;
  /** FK to tn_banks: payer's bank (for received chèques/traites) */
  payerBankId: string | null;
  /** Payer's RIB/IBAN (for received chèques/traites — complements payerBankId) */
  payerRib: string | null;
  /** Beneficiary name (issued: who to pay; received: payer name override) */
  beneficiaryName: string | null;
  /** Traite-specific: lieu de paiement (lieu unique — aussi imprimé comme lieu de création) */
  placeOfPayment: string | null;
  /**
   * Traite-specific: lieu d'émission — LEGACY. Le formulaire ne le saisit
   * plus (fusionné avec placeOfPayment) ; conservé en lecture pour les
   * traites historiques.
   */
  placeOfIssue: string | null;
  /**
   * Traite-specific: true = X sur "Oui", false = X sur "Non",
   * null = non renseigné → aucune croix imprimée (champ optionnel).
   */
  protestable: boolean | null;
  amount: number;
  issueDate: string;
  dueDate: string;
  /** Traite-specific: date d'acceptation */
  acceptanceDate: string | null;
  thirdPartyType: 'client' | 'supplier';
  thirdPartyId: string;
  currentState: CheckDraftState;
  /**
   * Date the instrument was successfully settled (cashed for received,
   * debited for issued) — the `event_date` of the transition into that state.
   * Null while still pending, or when it failed (bounced/rejected).
   * Derived from check_draft_events; used to measure the real settlement delay.
   */
  settledAt: string | null;
  /** True if the check/draft has been endorsed to a third party */
  isEndorsed: boolean;
  /** Name of the endorsee if is_endorsed = true */
  endorsedTo: string | null;
  paymentId: string | null;
  /** Timestamp of last PDF print */
  printedAt: string | null;
  notes: string | null;
  createdAt: string;
}

/** Immutable event record for state transitions */
export interface CheckDraftEvent {
  id: string;
  checkDraftId: string;
  fromState: CheckDraftState | null;
  toState: CheckDraftState;
  /** Date the transition took effect (business date, may differ from createdAt) */
  eventDate: string;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface CreateCheckDraftInput {
  type: CheckDraftType;
  direction: CheckDraftDirection;
  number: string;
  bankName?: string;
  bankAccountId?: string | null;
  payerBankId?: string | null;
  payerRib?: string | null;
  beneficiaryName?: string;
  placeOfPayment?: string;
  /** LEGACY — plus saisi par les formulaires ; accepté pour compat API. */
  placeOfIssue?: string;
  /** Optionnel tri-état : null/absent = case Oui/Non laissée vide à l'impression. */
  protestable?: boolean | null;
  amount: number;
  issueDate: string;
  dueDate: string;
  acceptanceDate?: string | null;
  thirdPartyType: 'client' | 'supplier';
  thirdPartyId: string;
  currentState?: CheckDraftState;
  isEndorsed?: boolean;
  endorsedTo?: string;
  paymentId?: string;
  notes?: string;
}

export type UpdateCheckDraftInput = Partial<
  Omit<CreateCheckDraftInput, 'type' | 'direction'>
>;

export interface CheckDraftFilters {
  type?: CheckDraftType;
  direction?: CheckDraftDirection;
  currentState?: CheckDraftState;
  thirdPartyType?: 'client' | 'supplier';
  thirdPartyId?: string;
  bankAccountId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  dueBefore?: string;
  dueAfter?: string;
}

export interface TransitionStateOptions {
  eventDate?: string;
  notes?: string;
}

// ---- Paramètres ----

export type TicketPriceType = 'ht' | 'ttc';

export type TicketElementKey =
  | 'companyName'
  | 'name'
  | 'reference'
  | 'price'
  | 'barcode';

export type SidebarBrandDisplay = 'logo_only' | 'logo_with_name';

/** Une entrée du registre `released_document_numbers`. */
export interface ReleasedDocumentNumber {
  id: string;
  /** Numéro complet tel qu'il était porté par le document supprimé. */
  number: string;
  /** Sa partie numérique, pour trier et comparer. */
  value: number;
  releasedAt: string;
}

/**
 * Ce que l'écran de numérotation montre d'une série : les numéros qui vont
 * revenir, et ceux qu'on peut proposer d'y ajouter.
 *
 * La distinction est tout l'enjeu du registre :
 *  - `pending` = numéros CONSIGNÉS par une suppression faite dans l'app. Sûrs :
 *    le document a existé ici. Ils reviennent tout seuls (si le réglage est
 *    actif), sans action de l'utilisateur.
 *  - `reclaimable` = trous simplement CONSTATÉS dans la plage émise, jamais
 *    consignés — antérieurs au registre, ou nés d'une suppression hors app.
 *    Rien ne prouve qu'un document les ait portés (une reprise d'historique
 *    partielle en fabrique autant qu'elle veut), donc ils ne reviennent JAMAIS
 *    d'eux-mêmes : l'utilisateur doit les inscrire explicitement.
 */
export interface ReleasedNumbersReport {
  docType: SalesDocType;
  /** Consignés et toujours libres — reviendront aux prochaines créations. */
  pending: ReleasedDocumentNumber[];
  /** Trous constatés dans la plage émise, à inscrire manuellement. */
  reclaimable: string[];
  /** Combien de trous constatés en tout (`reclaimable` peut être tronqué). */
  reclaimableTotal: number;
  /** Premier / dernier numéro vivant : les bornes du scan, affichées à l'écran. */
  firstNumber: string | null;
  lastNumber: string | null;
  /**
   * Plages écartées du scan parce que trop longues pour être des suppressions
   * — typiquement le vide entre deux millésimes d'une numérotation continue.
   * Exposé pour que l'écran le DISE : retirer 199 444 numéros de la liste sans
   * l'annoncer ferait passer une troncature massive pour une série propre.
   */
  skippedRuns: number;
  skippedValues: number;
}

export interface CompanySettings {
  id: string;
  companyName: string;
  softwareName: string | null;
  taxId: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  logoSmallUrl: string | null;
  /** Logo shown on generated PDF documents. Independent from logoUrl
   *  (dashboard sidebar). NULL = falls back to logoUrl. */
  pdfLogoUrl: string | null;
  /** Logo shown on the ERP dashboard /login page (light mode).
   *  Independent from logoUrl — never falls back to it. NULL = Gesty default. */
  loginLogoUrl: string | null;
  /** Logo shown on the ERP dashboard /login page (dark mode).
   *  Independent from logoDarkUrl — never falls back to it. NULL = Gesty default. */
  loginLogoDarkUrl: string | null;
  sidebarBrandDisplay: SidebarBrandDisplay;
  primaryColor: string | null;
  borderRadius: string | null;
  themeMode: string | null;
  themePreset: string | null;
  defaultVatRate: number;
  withholdingThreshold: number;
  withholdingRate: number;
  fiscalStampAmount: number;
  /** FODEC sur les documents de vente : désactivé par défaut. Quand il est
   *  actif, chaque nouveau document de vente porte `fodecRate` % du HT net,
   *  inclus dans l'assiette de la TVA (voir calculateDocumentTotals). */
  fodecEnabled: boolean;
  /** Taux FODEC en % (1 par défaut). */
  fodecRate: number;
  defaultCurrency: string;
  reuseDeletedNumbers: boolean;
  showClientCarNumber: boolean;
  // Extended branding — all optional, rendered on PDFs when present
  tagline: string | null;
  subtitle: string | null;
  website: string | null;
  fax: string | null;
  rib: string | null;
  bankName: string | null;
  socialFacebook: string | null;
  socialInstagram: string | null;
  socialLinkedin: string | null;
  ticketWidthMm: number;
  ticketHeightMm: number;
  ticketShowName: boolean;
  ticketShowPrice: boolean;
  ticketPriceType: TicketPriceType;
  ticketShowReference: boolean;
  ticketShowBarcode: boolean;
  ticketShowCompanyName: boolean;
  ticketCurrencySymbol: string;
  ticketElementOrder: TicketElementKey[];
  // ---- Auto-increment codes ----
  clientCodePrefix: string;
  clientCodeNext: number;
  clientCodePadding: number;
  supplierCodePrefix: string;
  supplierCodeNext: number;
  supplierCodePadding: number;
  articleCodePrefix: string;
  articleCodeNext: number;
  articleCodePadding: number;
  barcodePrefix: string;
  barcodeNext: number;
  barcodePadding: number;
  // ---- Document numbering (sales documents) ----
  // Prefix + length per sales doc type. The "next number" is NOT here — it
  // lives in tenant_sequence_state and is read/raised via the sequence RPCs.
  /** true = format légal {PREFIX}-{YEAR}-{NNNN} ; false = numérotation
   *  continue {PREFIX}-{NNNN} (reprise d'un ancien système). S'applique à
   *  tous les numéros générés (ventes, paiements, stock, RAS, caisse). */
  docNumberIncludeYear: boolean;
  quoteDocPrefix: string;
  quoteDocPadding: number;
  orderDocPrefix: string;
  orderDocPadding: number;
  deliveryDocPrefix: string;
  deliveryDocPadding: number;
  invoiceDocPrefix: string;
  invoiceDocPadding: number;
  creditNoteDocPrefix: string;
  creditNoteDocPadding: number;
}

export interface UpdateCompanySettingsInput {
  companyName?: string;
  softwareName?: string;
  taxId?: string;
  address?: string;
  phone?: string;
  email?: string;
  logoUrl?: string | null;
  logoDarkUrl?: string | null;
  faviconUrl?: string | null;
  logoSmallUrl?: string | null;
  pdfLogoUrl?: string | null;
  loginLogoUrl?: string | null;
  loginLogoDarkUrl?: string | null;
  sidebarBrandDisplay?: SidebarBrandDisplay;
  primaryColor?: string;
  borderRadius?: string;
  themeMode?: string;
  themePreset?: string;
  defaultVatRate?: number;
  withholdingThreshold?: number;
  withholdingRate?: number;
  fiscalStampAmount?: number;
  fodecEnabled?: boolean;
  fodecRate?: number;
  reuseDeletedNumbers?: boolean;
  showClientCarNumber?: boolean;
  tagline?: string;
  subtitle?: string;
  website?: string;
  fax?: string;
  rib?: string;
  bankName?: string;
  socialFacebook?: string;
  socialInstagram?: string;
  socialLinkedin?: string;
  ticketWidthMm?: number;
  ticketHeightMm?: number;
  ticketShowName?: boolean;
  ticketShowPrice?: boolean;
  ticketPriceType?: TicketPriceType;
  ticketShowReference?: boolean;
  ticketShowBarcode?: boolean;
  ticketShowCompanyName?: boolean;
  ticketCurrencySymbol?: string;
  ticketElementOrder?: TicketElementKey[];
  clientCodePrefix?: string;
  clientCodeNext?: number;
  clientCodePadding?: number;
  supplierCodePrefix?: string;
  supplierCodeNext?: number;
  supplierCodePadding?: number;
  articleCodePrefix?: string;
  articleCodeNext?: number;
  articleCodePadding?: number;
  barcodePrefix?: string;
  barcodeNext?: number;
  barcodePadding?: number;
  // ---- Document numbering (sales documents) ----
  docNumberIncludeYear?: boolean;
  quoteDocPrefix?: string;
  quoteDocPadding?: number;
  orderDocPrefix?: string;
  orderDocPadding?: number;
  deliveryDocPrefix?: string;
  deliveryDocPadding?: number;
  invoiceDocPrefix?: string;
  invoiceDocPadding?: number;
  creditNoteDocPrefix?: string;
  creditNoteDocPadding?: number;
}

export interface ModuleSetting {
  moduleKey: string;
  isEnabled: boolean;
}

/** Per-user module override row. When present, takes precedence over the
 *  tenant-level `module_settings.is_enabled` for that user. Lets us grant
 *  (or revoke) a single module to a single user without flipping it
 *  tenant-wide. */
export interface UserModuleOverride {
  userId: string;
  moduleKey: string;
  isEnabled: boolean;
}

// ---- User Preferences ----

export interface UserPreference {
  id: string;
  userId: string;
  preferenceKey: string;
  preferenceValue: unknown;
}

// ---- Auth ----

export interface AuthUser {
  id: string;
  email: string;
  role: string | null;
  /** Human display name when the backend knows one (e.g. Supabase
   *  user_metadata first_name/full_name). Optional so existing adapters
   *  and mocks keep compiling; consumers must fall back to the email. */
  displayName?: string | null;
  /** Contact phone captured during signup (E.164). Historical accounts may
   * not have one; onboarding copies it onto the tenant when available. */
  phone?: string | null;
  /**
   * Adresse demandée par un changement d'e-mail encore NON confirmé.
   *
   * Le changement d'e-mail n'est pas immédiat : le backend envoie un lien à
   * l'ancienne adresse ET un à la nouvelle, et `email` ne bouge que lorsque
   * les deux ont été ouverts. Entre-temps la nouvelle adresse vit ici — c'est
   * ce qui permet à l'écran Sécurité d'afficher « en attente de confirmation »
   * au lieu de laisser croire que le changement a échoué.
   */
  pendingEmail?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthSession {
  user: AuthUser;
  accessToken: string;
  /** Stable for refreshes, unique for each explicit sign-in session. */
  sessionGeneration?: string | null;
}

// ---- Retenue à la source ----

export type WithholdingCertificateType = 'sale' | 'purchase';

/** Type d'opération RS — miroir de rs_operation_types en BDD */
export interface RSOperationType {
  id: string;
  code: string; // ex "RS1_000001"
  labelFr: string;
  labelAr: string | null;
  labelEn: string | null;
  defaultRate: number; // %, ex 1.00 / 3.00 / 15.00
  threshold: number | null;
  appliesToSale: boolean;
  appliesToPurchase: boolean;
  displayOrder: number;
  isActive: boolean;
}

export interface WithholdingCertificateLine {
  id: string;
  certificateId: string;
  lineNumber: number;
  /** Snapshot du code Tej au moment de la saisie (ex "RS1_000001") */
  idTypeOperation: string;
  /** FK vers rs_operation_types.id — nullable pour rétrocompat */
  operationTypeId: string | null;
  /** Objet joint — présent si la requête inclut la jointure */
  operationType?: RSOperationType;
  anneeFacturation: number;
  cnpc: number;
  pCharge: number;
  montantHt: number;
  tauxRs: number;
  tauxTva: number;
  montantTva: number;
  montantTtc: number;
  montantRs: number;
  montantNetServi: number;
}

export interface WithholdingCertificate {
  id: string;
  number: string;
  certificateType: WithholdingCertificateType;
  thirdPartyType: 'client' | 'supplier';
  thirdPartyId: string;
  /** @deprecated — conservé pour rétrocompat ; utiliser sourceDocumentIds */
  sourceDocumentId: string | null;
  /** Liste complète des factures regroupées par cette retenue (N factures, même tiers) */
  sourceDocumentIds: string[];
  datePayment: string;
  refCertif: string;
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  totalRas: number;
  totalNetServi: number;
  declarationId: string | null;
  paymentId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  lines?: WithholdingCertificateLine[];
}

export interface CreateWithholdingCertificateInput {
  certificateType: WithholdingCertificateType;
  thirdPartyType: 'client' | 'supplier';
  thirdPartyId: string;
  /** Une ou plusieurs factures sources (même tiers obligatoire) */
  sourceDocumentIds: string[];
  datePayment: string;
  refCertif: string;
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  totalRas: number;
  totalNetServi: number;
  notes?: string;
  lines: Omit<
    WithholdingCertificateLine,
    'id' | 'certificateId' | 'lineNumber' | 'operationType'
  >[];
}

export interface UpdateWithholdingCertificateInput {
  datePayment?: string;
  refCertif?: string;
  notes?: string;
  paymentId?: string | null;
  declarationId?: string | null;
}

export interface WithholdingCertificateFilters {
  certificateType?: WithholdingCertificateType;
  thirdPartyId?: string;
  dateFrom?: string;
  dateTo?: string;
  declarationId?: string | null;
  isPaid?: boolean;
}

export interface WithholdingDeclaration {
  id: string;
  number: string;
  declarationType: WithholdingCertificateType;
  acteDepot: number;
  anneeDepot: number;
  moisDepot: number;
  isExported: boolean;
  exportedAt: string | null;
  /** Snapshot du matricule fiscal déclarant au moment de l'export */
  declarantMatriculeFiscal: string | null;
  /** Snapshot du XML exporté pour audit */
  tejXmlContent: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  certificates?: WithholdingCertificate[];
}

export interface CreateWithholdingDeclarationInput {
  declarationType: WithholdingCertificateType;
  acteDepot?: number;
  anneeDepot: number;
  moisDepot: number;
  notes?: string;
  certificateIds: string[];
}

export interface WithholdingDeclarationFilters {
  declarationType?: WithholdingCertificateType;
  anneeDepot?: number;
  moisDepot?: number;
  isExported?: boolean;
}

// ---- PDF templates ----

export type PdfTemplateDocScope = 'sales' | 'purchase' | 'stock' | 'pro';

export interface PdfTemplateRow {
  id: string;
  docScope: PdfTemplateDocScope;
  docType: string;
  name: string;
  designKey: 'classic' | 'modern' | 'minimal' | 'tunisien';
  options: Record<string, unknown>;
  isDefault: boolean;
  isActive: boolean;
  /** The model used for every document type without its own specific model.
   *  Exactly one per tenant. */
  isGlobalDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePdfTemplateInput {
  docScope: PdfTemplateDocScope;
  docType: string;
  name: string;
  designKey: 'classic' | 'modern' | 'minimal' | 'tunisien';
  options?: Record<string, unknown>;
}

export interface UpdatePdfTemplateInput {
  name?: string;
  designKey?: 'classic' | 'modern' | 'minimal' | 'tunisien';
  options?: Record<string, unknown>;
  isActive?: boolean;
  isGlobalDefault?: boolean;
}

// ---- Caisse (cash register) ----

export type CashMovementType =
  | 'opening'
  | 'sale_receipt'
  | 'payment_receipt'
  | 'manual_deposit'
  | 'withdrawal'
  | 'closing'
  /** Constat de comptage physique — aucun effet sur le solde théorique. */
  | 'count'
  /** Régularisation signée (négatif = manquant constaté). */
  | 'adjustment';

/** Comment une session a été ouverte / fermée. */
export type CashSessionMode = 'manual' | 'auto';

/** Politique de report du fond de caisse d'un jour sur l'autre. */
export type CashFloatCarryMode = 'carry' | 'fixed' | 'zero';

/**
 * Instantané statistique dénormalisé posé sur la session par le domaine.
 * Permet d'agréger une année sans relire tous les paiements et lignes de vente.
 */
export interface CashSessionStats {
  totalReceipts: number;
  cashReceipts: number;
  ticketCount: number;
  salesAmount: number;
  profit: number;
  byMethod: Record<string, { total: number; count: number }>;
  /**
   * Encaissements ventilés par heure de la journée, clés `'0'`…`'23'`.
   * Doit rester aligné sur `CashSessionStats` du domaine.
   *
   * `undefined` = ventilation INCONNUE (instantané figé avant l'arrivée de la
   * carte d'affluence), jamais « journée sans vente ».
   */
  byHour?: Record<string, { total: number; count: number }>;
  computedAt: string;
}

/**
 * Détail d'un comptage de tiroir : combien de coupures de chaque sorte.
 *
 * Clé = identifiant STABLE de coupure (`note-50000`, `coin-100`…), valeur =
 * QUANTITÉ entière ≥ 1, jamais un montant. Les quantités nulles sont absentes ;
 * un objet vide veut dire « aucun détail saisi », pas « tiroir vide ».
 *
 * Table des coupures, somme (en millimes entiers) et nettoyage :
 * `libs/domain/caisse/denominations.ts`.
 */
export type CashCountBreakdown = Readonly<Record<string, number>>;

export interface CashSession {
  id: string;
  number: string;
  bankAccountId: string;
  openedBy: string;
  openedAt: string;
  openingBalance: number;
  /** Détail par coupures du fond d'ouverture. `null` = saisi en montant global. */
  openingBreakdown: CashCountBreakdown | null;
  closedBy: string | null;
  closedAt: string | null;
  expectedBalance: number | null;
  /** `null` = tiroir JAMAIS compté (clôture automatique). Jamais 0 par défaut. */
  countedBalance: number | null;
  /**
   * Détail par coupures du DERNIER comptage. `null` = compté en montant global,
   * ou jamais compté. Quand il existe, il somme EXACTEMENT à `countedBalance` —
   * l'adapter recalcule le montant depuis le détail, les deux ne peuvent donc
   * pas se contredire en base.
   */
  countedBreakdown: CashCountBreakdown | null;
  difference: number | null;
  isClosed: boolean;
  notes: string | null;
  /** Journée commerciale de rattachement, `YYYY-MM-DD`. */
  sessionDate: string;
  openMode: CashSessionMode;
  closeMode: CashSessionMode | null;
  countedAt: string | null;
  countedBy: string | null;
  stats: CashSessionStats | null;
  createdAt: string;
  updatedAt: string;
}

/** Réglages de caisse d'un tenant (table `caisse_settings`). */
export interface CaisseSettings {
  sessionMode: 'auto' | 'manual';
  dayCutoffMinutes: number;
  floatCarryMode: CashFloatCarryMode;
  fixedFloat: number;
  countReminder: boolean;
  autoAttachCashPayments: boolean;
}

export type UpdateCaisseSettingsInput = Partial<CaisseSettings>;

export interface CashMovement {
  id: string;
  number: string;
  sessionId: string;
  movementType: CashMovementType;
  category: string | null;
  amount: number;
  description: string | null;
  /**
   * Détail par coupures du comptage à l'origine de ce mouvement (`opening`,
   * `count`, `closing`) — `null` partout ailleurs. C'est l'ARCHIVE : la session
   * ne garde que le dernier comptage, le journal garde tous les comptages.
   */
  breakdown: CashCountBreakdown | null;
  paymentId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface OpenCashSessionInput {
  bankAccountId: string;
  openingBalance: number;
  /**
   * Détail par coupures du fond compté à l'ouverture. Quand il est fourni et
   * non vide, c'est LUI qui fait foi : `openingBalance` est recalculé depuis le
   * détail pour que le montant et son détail ne puissent pas diverger.
   */
  openingBreakdown?: CashCountBreakdown | null;
  notes?: string;
  openedBy?: string;
  /** Journée commerciale visée (`YYYY-MM-DD`). Calculée par le domaine. */
  sessionDate?: string;
  /** `auto` pose l'idempotence par journée (index `uq_cash_sessions_auto_day`). */
  openMode?: CashSessionMode;
  /** Instant d'ouverture forcé — utilisé par le rattrapage automatique. */
  openedAt?: string;
}

export interface CloseCashSessionInput {
  sessionId: string;
  /**
   * Montant physiquement compté. `null` = clôture SANS comptage : l'écart reste
   * inconnu au lieu d'être faussement nul.
   */
  countedBalance: number | null;
  /**
   * Détail par coupures du comptage de clôture. Quand il est fourni et non vide,
   * c'est LUI qui fait foi : `countedBalance` est recalculé depuis le détail.
   */
  countedBreakdown?: CashCountBreakdown | null;
  expectedBalance: number;
  notes?: string;
  closedBy?: string;
  closeMode?: CashSessionMode;
  /** Instant de clôture forcé (fin de la journée commerciale en rattrapage). */
  closedAt?: string;
  /** Instantané statistique à figer sur la session au moment de la clôture. */
  stats?: CashSessionStats | null;
}

/** Enregistrement d'un comptage physique — a posteriori ou en cours de journée. */
export interface RecordCashCountInput {
  /** Durable idempotency key for this physical count intent. */
  clientRequestId?: string;
  sessionId: string;
  countedBalance: number;
  /**
   * Détail par coupures du comptage. Quand il est fourni et non vide, c'est LUI
   * qui fait foi : `countedBalance` est recalculé depuis le détail, et l'écart
   * éventuellement régularisé se déduit du montant ainsi recalculé.
   */
  countedBreakdown?: CashCountBreakdown | null;
  expectedBalance: number;
  countedBy?: string;
  notes?: string;
  /** Crée aussi un mouvement `adjustment` qui réaligne le théorique sur le réel. */
  createAdjustment?: boolean;
}

export interface CreateCashMovementInput {
  /** Durable idempotency key; mobile/offline callers persist it per intent. */
  clientRequestId?: string;
  sessionId: string;
  movementType: 'manual_deposit' | 'withdrawal' | 'count' | 'adjustment';
  category: string;
  amount: number;
  description?: string;
  /** Détail par coupures, sur un mouvement de comptage uniquement. */
  breakdown?: CashCountBreakdown | null;
  createdBy?: string;
}

export interface CashSessionFilters {
  bankAccountId?: string;
  isClosed?: boolean;
  dateFrom?: string;
  dateTo?: string;
  /** Bornes INCLUSIVES sur la journée commerciale (`YYYY-MM-DD`). */
  fromBusinessDay?: string;
  toBusinessDay?: string;
}

// ---- Ressources (bibliothèque de documents internes) ----

export interface ResourceFolder {
  id: string;
  parentId: string | null;
  name: string;
  /** Couleur d'organisation (hex court, ex. `#F59E0B`) ou null. */
  color: string | null;
  /** Dossier « ProSpace » : son contenu est publié dans l'Espace Pro
   *  (électricien + mode client). Un seul par tenant, créé automatiquement,
   *  non supprimable et non déplaçable. */
  isProSpace: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateResourceFolderInput {
  name: string;
  parentId?: string | null;
  color?: string | null;
}

export interface UpdateResourceFolderInput {
  name?: string;
  parentId?: string | null;
  color?: string | null;
}

export interface ResourceFile {
  id: string;
  folderId: string | null;
  name: string;
  storagePath: string;
  mimeType: string | null;
  sizeBytes: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateResourceFileInput {
  folderId: string | null;
  name: string;
  storagePath: string;
  mimeType: string | null;
  sizeBytes: number;
}

export interface UpdateResourceFileInput {
  name?: string;
  folderId?: string | null;
}

export interface ResourceFileFilters {
  folderId?: string | null;
  /** Recherche plein-texte sur le nom. */
  search?: string;
}

/**
 * Une entrée du catalogue partagé ProSpace, telle que servie par la RPC
 * `pro_shared_catalog()` : un fichier de la sous-arborescence du dossier
 * « ProSpace » du tenant AUQUEL LE PRO APPARTIENT.
 *
 * Passe par une RPC et non par une lecture de table : les policies RLS
 * s'additionnent, et un compte à la fois membre d'un tenant et pro d'un autre
 * verrait sinon la bibliothèque interne de son propre tenant remonter dans le
 * catalogue montré au client.
 */
export interface ProSpaceCatalogEntry {
  fileId: string;
  fileName: string;
  storagePath: string;
  mimeType: string | null;
  sizeBytes: number;
  folderId: string | null;
  folderName: string | null;
  /** Posé directement dans « ProSpace » plutôt que dans un sous-dossier. */
  inRoot: boolean;
  createdAt: string;
  updatedAt: string;
}
